import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import StaffHeader from '../components/StaffHeader';
import TrialExpired from '../components/TrialExpired';
import { useConfig } from '../context/ConfigContext';
import usePullToRefresh from '../hooks/usePullToRefresh';
import { formatPhoneDisplay } from '../utils/phoneFormat';
import { formatCurrency } from '../utils/formatCurrency';
import { hasStopPrice, stopTotal, NO_PRICE_LABEL, isUnpricedLockedStop, lockedStopTotal, lockedStopSubtotal } from '../utils/stopPrice';
import { addonPriceLabel, addonsAmount, stopAddonSelection, stopAddonsPayload, customerAddonLines } from '../utils/stopAddons';
import { ConfirmModal, AlertModal } from '../components/ConfirmModal';
import { isDoNotServeConflict, confirmDoNotServe } from '../utils/doNotServe';
import DoNotServeBadge from '../components/DoNotServeBadge';
import CompletionPaymentChoices from '../components/CompletionPaymentChoices';
import { DEFAULT_PAYMENT_METHODS, isSquare, methodLabel, preferredMethodOptions } from '../utils/paymentMethods';
import {
  loadRouteRequests, withRequestFlags, requestBadgeText, confirmNoRequest, NO_REQUEST_LABEL, preferenceBadge,
} from '../utils/serviceRequests';
import { companyToday, inZone } from '../utils/companyDate';
import { arrivalIndicator, canSendArrival, mergeArrivalStates, skipIndicator, tapArrivalEntries } from '../utils/arrivalAlert';
import DepthEntryModal, { billStopDifference, doorPaymentErrorMessage } from '../components/DepthEntryModal';
import {
  isPendingDepthStop, AWAITING_DEPTH_LABEL, stopDepthBand, snowServiceLabel, stopPremiumPercent, premiumLineLabel,
} from '../utils/stopPrice';

function StormMode() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, isDriver, isSuperAdmin, isOnTrial, isTrialExpired, isPro, shouldHidePrices, refreshUser, timezone } = useAuth();
  const { getAppName } = useConfig();

  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null);
  // Stop ids with an on-my-way request in flight (SMS Phase 5).
  const [arrivalSending, setArrivalSending] = useState({});
  const [showPrintView, setShowPrintView] = useState(false);
  const [routeFees, setRouteFees] = useState(null);
  const printRef = useRef();

  // STORM DEPTH (depth pricing 3.3, 3.4). The depth modal; the routes it has
  // already opened itself on (once per route per visit); and whether the last
  // completion or skip on this screen armed that auto-open.
  const [showDepthModal, setShowDepthModal] = useState(false);
  const depthAutoOpened = useRef(new Set());
  const depthAutoOpenArmed = useRef(false);
  // Door payment on a stop awaiting depth: the amount collected, and why a
  // paid choice was refused before anything was sent.
  const [amountCollected, setAmountCollected] = useState('');
  const [doorPaymentError, setDoorPaymentError] = useState('');
  // Bill the difference on a stale-invoice stop.
  const [billingDiffStopId, setBillingDiffStopId] = useState(null);
  const [diffResults, setDiffResults] = useState({});

  // Route filter state (active/completed/archived)
  const [routeFilter, setRouteFilter] = useState('active');

  // Route sort order (newest/oldest) - persisted to localStorage
  const [routeSortOrder, setRouteSortOrder] = useState(() => {
    return localStorage.getItem('snowtrack_route_sort') || 'newest';
  });

  // Add customer mid-route state
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [availableCustomers, setAvailableCustomers] = useState([]);
  // GET /api/routes/:id/service-requests, read when Add Customer opens.
  const [routeRequests, setRouteRequests] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [reoptimizing, setReoptimizing] = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(false);

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [pendingPaymentStop, setPendingPaymentStop] = useState(null);
  const [isEditingPayment, setIsEditingPayment] = useState(false);

  // Discount state for payment modal
  const [showDiscountForm, setShowDiscountForm] = useState(false);
  const [stopDiscount, setStopDiscount] = useState({ amount: '', type: '', reason: '', ongoing: false });

  // Extra charge state for payment modal
  const [showExtraChargeForm, setShowExtraChargeForm] = useState(false);
  const [extraCharge, setExtraCharge] = useState({ amount: '', reason: '' });

  // Comp/free stop state
  const [isComp, setIsComp] = useState(false);
  const [compReason, setCompReason] = useState('');

  // Photo upload state
  const [stopPhotos, setStopPhotos] = useState([]);
  // True from the moment a photo is chosen until it is compressed, which takes
  // a second or two on a phone and would otherwise show nothing.
  const [photoPreparing, setPhotoPreparing] = useState(false);
  // Photos taken but not yet saved: uploading, or failed and waiting for Retry.
  // Keyed by stop and kept across closing the modal, so a failed photo is still
  // there to retry when the stop is reopened.
  const [pendingPhotos, setPendingPhotos] = useState([]);
  // The stop whose photos are showing, so a late upload for another stop is not
  // added to this one's thumbnails.
  const photoStopIdRef = useRef(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const photoInputRef = useRef(null);

  // Service add-ons state
  const [availableAddons, setAvailableAddons] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState({}); // { addonId: true/false }

  // Salt applied state for "as_needed" customers
  const [saltAppliedToggle, setSaltAppliedToggle] = useState(false);

  // Route expenses for summary report
  const [routeExpenses, setRouteExpenses] = useState([]);

  // Referral discount state - maps customer_id to discount info
  const [referralDiscounts, setReferralDiscounts] = useState({});

  // Phone action popup state
  const [phonePopup, setPhonePopup] = useState({ show: false, phone: '', x: 0, y: 0 });
  const phonePopupRef = useRef(null);

  // Billing modal state
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [billingInProgress, setBillingInProgress] = useState(false);
  const [billingResults, setBillingResults] = useState(null);
  const [selectedBillStops, setSelectedBillStops] = useState(new Set());

  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState(DEFAULT_PAYMENT_METHODS);

  // Lead sources state
  const [leadSources, setLeadSources] = useState([
    { id: 'facebook', name: 'Facebook', enabled: true, isBuiltIn: true },
    { id: 'craigslist', name: 'Craigslist', enabled: true, isBuiltIn: true },
    { id: 'flyer', name: 'Flyer', enabled: true, isBuiltIn: true },
    { id: 'yard_sign', name: 'Yard Sign', enabled: true, isBuiltIn: true },
    { id: 'referral', name: 'Friend/Referral', enabled: true, isBuiltIn: true },
    { id: 'google', name: 'Google', enabled: true, isBuiltIn: true },
    { id: 'nextdoor', name: 'Nextdoor', enabled: true, isBuiltIn: true },
    { id: 'other', name: 'Other', enabled: true, isBuiltIn: true },
  ]);

  // Pricing tiers state
  const [pricingTiers, setPricingTiers] = useState([]);

  // Quick-add new customer state
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    email: '',
    // pricing_tiers id as a string, or 'custom'. Sent as snowTierId; the
    // server derives the legacy customers.tier value from it.
    snowTierId: 'custom',
    priceSnow: '50.00',
    saltPrice: '8.00',
    driveway: true,
    sidewalks: false,
    walkways: false,
    salt: false,
    paymentMethodPreferred: 'square',
    serviceFrequency: 'seasonal',
    notes: '',
    leadSource: ''
  });

  // GPS optimization state
  const [useGpsForOptimization, setUseGpsForOptimization] = useState(true);
  const [gpsStatus, setGpsStatus] = useState(''); // '', 'acquiring', 'success', 'unavailable'

  // Start route modal state
  const [showStartRouteModal, setShowStartRouteModal] = useState(false);
  const [pendingRouteToStart, setPendingRouteToStart] = useState(null);
  const [startLocationOption, setStartLocationOption] = useState('gps'); // 'gps' or 'saved'
  const [gpsStartLocation, setGpsStartLocation] = useState(null); // { lat, lon, address }
  const [gpsStartStatus, setGpsStartStatus] = useState(''); // '', 'detecting', 'success', 'error'
  const [startingRoute, setStartingRoute] = useState(false);

  const [alertModal, setAlertModal] = useState({ show: false, title: '', message: '' });
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null, confirmColor: '#1e88e5', confirmText: 'Confirm' });

  const showAlert = (message, title = '') => {
    setAlertModal({ show: true, title, message });
  };

  const showConfirm = (message, onConfirm, options = {}) => {
    setConfirmModal({
      show: true,
      title: options.title || 'Confirm',
      message,
      onConfirm: () => { setConfirmModal(prev => ({ ...prev, show: false })); onConfirm(); },
      confirmColor: options.confirmColor || '#1e88e5',
      confirmText: options.confirmText || 'Confirm',
    });
  };

  const loadRoutes = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/routes`);
      setRoutes(response.data);
    } catch (err) {
      console.error('Failed to load routes:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load pricing tiers
  const loadPricingTiers = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/pricing-tiers`);
      setPricingTiers(response.data);
      // Update default new customer tier and price based on first tier
      if (response.data.length > 0) {
        const firstTier = response.data[0];
        setNewCustomer(prev => ({
          ...prev,
          snowTierId: String(firstTier.id),
          priceSnow: parseFloat(firstTier.default_price).toFixed(2),
          saltPrice: parseFloat(firstTier.salt_price || 0).toFixed(2),
        }));
      }
    } catch (err) {
      console.error('Failed to load pricing tiers:', err);
    }
  }, []);

  // Load payment methods from settings
  const loadPaymentMethods = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/settings/payment-methods`);
      if (response.data && response.data.length > 0) {
        setPaymentMethods(response.data);
      }
    } catch (err) {
      console.error('Failed to load payment methods:', err);
    }
  }, []);

  const loadLeadSources = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/settings/lead-sources`);
      if (response.data && response.data.length > 0) {
        setLeadSources(response.data);
      }
    } catch (err) {
      console.error('Failed to load lead sources:', err);
    }
  }, []);

  // Load company settings for GPS optimization preference
  const loadCompanySettings = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/company`);
      if (response.data) {
        setUseGpsForOptimization(response.data.route_optimize_use_gps ?? true);
      }
    } catch (err) {
      console.error('Failed to load company settings:', err);
    }
  }, []);

  // Get only enabled lead sources
  const enabledLeadSources = leadSources.filter(s => s.enabled);

  // Load referral discounts for all customers in a route
  const loadReferralDiscounts = useCallback(async (routeData) => {
    if (!routeData?.stops || !routeData.storm_id) {
      setReferralDiscounts({});
      return;
    }

    try {
      const discounts = {};
      // Fetch discounts for ALL stops (not just pending) so print summary has accurate data
      const allStops = routeData.stops.filter(s => s.status !== 'skipped');
      await Promise.all(
        allStops.map(async (stop) => {
          try {
            const url = `${process.env.REACT_APP_API_URL}/api/referral-discount/${stop.customer_id}?stormId=${encodeURIComponent(routeData.storm_id)}`;
            const response = await axios.get(url);
            discounts[stop.customer_id] = response.data;
          } catch (err) {
            console.error('Failed to load referral discount:', err.message);
          }
        })
      );

      setReferralDiscounts(discounts);
    } catch (err) {
      console.error('Failed to load referral discounts:', err.message);
    }
  }, []);

  // Load service add-ons
  const loadServiceAddons = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/service-addons?product=snow`);
      if (response.data && response.data.length > 0) {
        setAvailableAddons(response.data.filter(a => a.is_active));
      }
    } catch (err) {
      console.error('Failed to load service add-ons:', err);
    }
  }, []);

  // Reverse geocode coordinates to address
  const reverseGeocode = async (lat, lon) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/weather/reverse-geocode?lat=${lat}&lon=${lon}`
      );
      return response.data.address || `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
    } catch (err) {
      console.error('Reverse geocode error:', err);
      return `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
    }
  };

  // Detect GPS location for route start
  const detectGpsForStart = useCallback(async () => {
    setGpsStartStatus('detecting');
    setGpsStartLocation(null);

    if (!navigator.geolocation) {
      setGpsStartStatus('error');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;
        const address = await reverseGeocode(lat, lon);
        setGpsStartLocation({ lat, lon, address });
        setGpsStartStatus('success');
      },
      (error) => {
        console.error('GPS error:', error);
        setGpsStartStatus('error');
        setStartLocationOption('saved'); // Fall back to saved location
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  }, []);

  // Select and load a route
  const selectRoute = useCallback(async (route) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/routes/${route.id}`
      );
      setSelectedRoute(response.data);

      // Load expenses for this route
      try {
        const expensesResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/expenses?routeId=${route.id}`
        );
        // Backend now filters by routeId, response is { expenses: [...] }
        setRouteExpenses(expensesResponse.data.expenses || []);
      } catch (expErr) {
        // Non-pro users or other errors - just skip expenses
        setRouteExpenses([]);
      }

      // Load referral discounts if route has storm_id
      if (response.data.storm_id) {
        await loadReferralDiscounts(response.data);
      } else {
        setReferralDiscounts({});
      }
    } catch (err) {
      console.error('Failed to load route details:', err);
    }
  }, [loadReferralDiscounts]);

  // Show start route modal for not-started routes
  const handleRouteClick = useCallback(async (route) => {
    if (route.status === 'not_started') {
      // Load full route details first
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/routes/${route.id}`
        );
        setPendingRouteToStart(response.data);
        setShowStartRouteModal(true);
        setStartLocationOption(useGpsForOptimization ? 'gps' : 'saved');
        setGpsStartLocation(null);
        setGpsStartStatus('');

        // Auto-detect GPS if preference is enabled
        if (useGpsForOptimization) {
          detectGpsForStart();
        }
      } catch (err) {
        console.error('Failed to load route for start modal:', err);
      }
    } else {
      // Route already started, just select it
      selectRoute(route);
    }
  }, [useGpsForOptimization, detectGpsForStart, selectRoute]);

  // Confirm and start route from modal
  const confirmStartRoute = async () => {
    if (!pendingRouteToStart) return;

    setStartingRoute(true);
    try {
      // Start the route. That is the whole of what this action means, so it is
      // the whole of what this request sends.
      //
      // The GPS choice in this modal is the origin for THIS RUN and is not the
      // route's configured starting address. It is never persisted: the origin
      // reaches the optimizer through the optimize-remaining request body, from
      // a fix reoptimizeRemaining takes itself at the moment it runs, which is
      // fresher than anything captured here. The configured starting address is
      // the user's setting, changed only by the user editing the route.
      //
      // This previously sent `{ ...pendingRouteToStart, status }`. The record
      // comes from GET /api/routes/:id as `SELECT r.*`, so its keys are
      // snake_case, while PUT /api/routes/:id reads camelCase. The spread
      // therefore sent keys the server ignores and omitted every key it writes,
      // which nulled service_type, storm_id and the four driver_pay_* columns
      // and forced product_type to 'snow' on every route start.
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/routes/${pendingRouteToStart.id}`,
        { status: 'in_progress' }
      );

      // Close modal and select route
      setShowStartRouteModal(false);
      setPendingRouteToStart(null);
      await selectRoute(pendingRouteToStart);
    } catch (err) {
      if (handleLapsedError(err)) {
        setShowStartRouteModal(false);
        setPendingRouteToStart(null);
        return;
      }
      console.error('Failed to start route:', err);
      showAlert('Failed to start route. Please try again.', 'Error');
    } finally {
      setStartingRoute(false);
    }
  };

  // Refresh function for pull-to-refresh - reloads routes and selected route
  const handleRefresh = useCallback(async () => {
    await loadRoutes();
    if (selectedRoute) {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
        );
        setSelectedRoute(response.data);
      } catch (err) {
        console.error('Failed to refresh selected route:', err);
      }
    }
  }, [loadRoutes, selectedRoute]);

  const { PullToRefreshIndicator } = usePullToRefresh(handleRefresh);

  useEffect(() => {
    loadRoutes();
    loadPricingTiers();
    loadPaymentMethods();
    loadLeadSources();
    loadCompanySettings();
    loadServiceAddons();
  }, [loadRoutes, loadPricingTiers, loadPaymentMethods, loadLeadSources, loadCompanySettings, loadServiceAddons]);

  // If we came here with a specific routeId, load that route
  useEffect(() => {
    if (location.state?.routeId && routes.length > 0) {
      const route = routes.find(r => r.id === location.state.routeId);
      if (route) {
        selectRoute(route);
      }
    }
  }, [location.state, routes, selectRoute]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (showAddCustomer) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showAddCustomer]);

  // Close phone popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (phonePopup.show && phonePopupRef.current && !phonePopupRef.current.contains(e.target)) {
        setPhonePopup({ show: false, phone: '', x: 0, y: 0 });
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [phonePopup.show]);

  // Handle phone click - show call/text popup
  const handlePhoneClick = (e, phone) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.target.getBoundingClientRect();
    setPhonePopup({
      show: true,
      phone: phone,
      x: rect.left,
      y: rect.bottom + 4
    });
  };

  // Toggle route sort order and save to localStorage
  const toggleRouteSortOrder = () => {
    const newOrder = routeSortOrder === 'newest' ? 'oldest' : 'newest';
    setRouteSortOrder(newOrder);
    localStorage.setItem('snowtrack_route_sort', newOrder);
  };

  // When the last stop on a depth route with stops awaiting depth is completed
  // or skipped here, the depth modal opens once on its own. Owner and admin
  // only (the depth endpoints are). Dismissing it does not reopen it.
  useEffect(() => {
    if (!depthAutoOpenArmed.current || !selectedRoute) return;
    const stops = selectedRoute.stops || [];
    const allDone = stops.length > 0 && stops.every(s => s.status === 'completed' || s.status === 'skipped');
    depthAutoOpenArmed.current = false;
    if (!allDone) return;
    const pending = selectedRoute.pending_depth_count ?? stops.filter(isPendingDepthStop).length;
    if (selectedRoute.pricing_mode === 'depth' && pending > 0 && isAdmin()
      && !depthAutoOpened.current.has(selectedRoute.id)) {
      depthAutoOpened.current.add(selectedRoute.id);
      setShowDepthModal(true);
    }
  }, [selectedRoute, isAdmin]);

  // DEPTH PRICING. Whether this stop has, or will have on completion, no price
  // until the storm depth is entered: completed and pending_depth, or not yet
  // completed on a depth route whose depth is still blank.
  const isDepthRoute = (route) => route?.pricing_mode === 'depth';
  const routeHasDepth = (route) => route?.snow_depth_inches !== null && route?.snow_depth_inches !== undefined;
  const stopAwaitsDepth = (stop) => {
    if (!stop) return false;
    if (isPendingDepthStop(stop)) return true;
    return stop.status !== 'completed' && isDepthRoute(selectedRoute) && !routeHasDepth(selectedRoute)
      && selectedRoute?.product_type !== 'lawn';
  };
  const pendingDepthCount = selectedRoute
    ? (selectedRoute.pending_depth_count ?? (selectedRoute.stops || []).filter(isPendingDepthStop).length)
    : 0;

  // The card price of a stop not yet completed on a depth route (design
  // 2.5), from GET /api/routes/:id/depth-price-preview: the range by depth
  // while the route has no depth, the band total once it has one. Keyed on
  // the route, its depth and its open stops, so a completion or a depth entry
  // refetches it and nothing else does.
  const [depthPreview, setDepthPreview] = useState({ key: null, byStop: {} });
  const depthPreviewKey = selectedRoute && isDepthRoute(selectedRoute) && !shouldHidePrices()
    ? `${selectedRoute.id}|${selectedRoute.snow_depth_inches ?? ''}|${(selectedRoute.stops || [])
      .filter(s => s.status !== 'completed').map(s => s.id).join(',')}`
    : null;
  useEffect(() => {
    if (!depthPreviewKey) return undefined;
    let cancelled = false;
    const routeId = depthPreviewKey.split('|')[0];
    axios.get(`${process.env.REACT_APP_API_URL}/api/routes/${routeId}/depth-price-preview`)
      .then(res => {
        if (cancelled) return;
        const byStop = {};
        (res.data.stops || []).forEach(p => { byStop[p.stopId] = p; });
        setDepthPreview({ key: depthPreviewKey, byStop });
      })
      .catch(() => { if (!cancelled) setDepthPreview({ key: depthPreviewKey, byStop: {} }); });
    return () => { cancelled = true; };
  }, [depthPreviewKey]);
  // The preview for one open stop, or null (flat route, not loaded yet, or a
  // lawn stop on a 'both' route): the card then shows its usual price.
  const depthPreviewLoading = Boolean(depthPreviewKey) && depthPreview.key !== depthPreviewKey;
  const depthPreviewFor = (stop) => {
    if (!depthPreviewKey || depthPreview.key !== depthPreviewKey) return null;
    const p = depthPreview.byStop[stop.id];
    return p && p.kind !== 'flat' ? p : null;
  };
  const formatDepthPreview = (p) => {
    if (p.kind === 'range') {
      return p.min === p.max ? `By depth: ${formatCurrency(p.min)}` : `By depth: ${formatCurrency(p.min)}-${formatCurrency(p.max)}`;
    }
    if (p.kind === 'band') return `${formatCurrency(p.total)}${p.bandLabel ? ` (${p.bandLabel})` : ''}`;
    if (p.kind === 'no_tier') return 'No size tier';
    return p.bandLabel ? `No price for ${p.bandLabel}` : 'No depth price set';
  };

  const refreshAfterDepth = async () => {
    loadRoutes();
    if (!selectedRoute?.id) return;
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`);
      setSelectedRoute(response.data);
    } catch (err) {
      console.error('Failed to reload route after depth:', err);
    }
  };

  const handleBillDifference = async (stop) => {
    setBillingDiffStopId(stop.id);
    const outcome = await billStopDifference(stop.id);
    setDiffResults(prev => ({ ...prev, [stop.id]: outcome }));
    setBillingDiffStopId(null);
    showAlert(outcome.message, outcome.ok ? 'Bill the difference' : 'Error');
    refreshAfterDepth();
  };

  // Categorize stops for billing
  const getBillingCategories = () => {
    const completedStops = selectedRoute?.stops?.filter(s => s.status === 'completed') || [];

    const categories = {
      autoBill: [], // Square + email + unpaid + no invoice
      noEmail: [], // Square + no email
      manual: [], // Non-Square payment methods
    };

    completedStops.forEach(stop => {
      // Skip if already paid or invoice already sent
      if (stop.payment_status === 'paid' || stop.invoice_sent_at) return;
      // Awaiting depth: no figure to bill yet (the server refuses Bill All
      // with DEPTH_REQUIRED until the depth is entered).
      if (isPendingDepthStop(stop)) return;

      const squareStop = isSquare(stop.payment_method);
      const hasEmail = !!stop.email;
      const amount = getEffectivePrice(stop);
      const customerName = `${stop.first_name || ''} ${stop.last_name || ''}`.trim();

      if (squareStop && hasEmail) {
        categories.autoBill.push({ ...stop, customerName, amount });
      } else if (squareStop && !hasEmail) {
        categories.noEmail.push({ ...stop, customerName, amount });
      } else if (stop.payment_method) {
        categories.manual.push({ ...stop, customerName, amount });
      }
    });

    return categories;
  };

  // Send invoices to all or selected Square customers
  const handleBillSelected = async (stopIds) => {
    if (!selectedRoute?.id) return;

    setBillingInProgress(true);
    setBillingResults(null);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/bill-all`,
        stopIds ? { stopIds } : {}
      );

      setBillingResults(response.data);
      setSelectedBillStops(new Set());

      const routeResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
      );
      setSelectedRoute(routeResponse.data);
    } catch (error) {
      console.error('Billing error:', error);
      // Show the server's own message when it sent one - a 403 from the
      // platform invoicing guard or a tier gate explains itself far better
      // than a generic failure line.
      setBillingResults({
        sent: [],
        failed: [],
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to send invoices'
      });
    } finally {
      setBillingInProgress(false);
    }
  };

  // Send invoices to all Square customers
  const handleBillAll = async () => {
    if (!selectedRoute?.id) return;

    setBillingInProgress(true);
    setBillingResults(null);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/bill-all`
      );

      setBillingResults(response.data);

      // Refresh the route to get updated invoice_sent_at
      const routeResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
      );
      setSelectedRoute(routeResponse.data);
    } catch (error) {
      console.error('Billing error:', error);
      // Same as handleBillSelected: surface the server's message verbatim.
      setBillingResults({
        sent: [],
        failed: [],
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to send invoices'
      });
    } finally {
      setBillingInProgress(false);
    }
  };

  // New work refused because the company is lapsed: the free trial storm is
  // over (TRIAL_EXPIRED) or the subscription has ended (SUBSCRIPTION_LAPSED).
  // Never shown as a generic failure: close the payment modal, refresh the user
  // so every surface agrees with the server, and show the upgrade prompt or the
  // server's own sentence. Returns true when it handled the error, so the caller
  // can stop there.
  const handleLapsedError = (err) => {
    const code = err?.response?.data?.code;
    if (code !== 'TRIAL_EXPIRED' && code !== 'SUBSCRIPTION_LAPSED') return false;
    setShowPaymentModal(false);
    refreshUser({ silent: true });
    if (code === 'TRIAL_EXPIRED') {
      setShowTrialModal(true);
    } else {
      showAlert(err.response.data.error, 'Subscription ended');
    }
    return true;
  };

  // Show payment modal when Done is clicked
  const markStopComplete = (stop) => {
    // A route already started in the trial storm can always be finished, so
    // only a route not yet started is stopped here once the storm is over.
    if (isTrialExpired() && !selectedRoute?.storm_event_counted) {
      setShowTrialModal(true);
      return;
    }
    setPendingPaymentStop(stop);
    setIsEditingPayment(false);
    // Reset discount form
    setShowDiscountForm(false);
    setStopDiscount({ amount: '', type: '', reason: '', ongoing: false });
    // Reset extra charge form
    setShowExtraChargeForm(false);
    setExtraCharge({ amount: '', reason: '' });
    // Initialize salt toggle based on customer preference
    // For "as_needed", default to false; for "always", default to true
    setSaltAppliedToggle(stop.salt_preference === 'always' || (stop.salt_preference !== 'as_needed' && stop.salt === true));
    // Start ticked from the add-ons on the stop (picked up from the customer
    // when the stop was added); the driver can untick them.
    setSelectedAddons(stopAddonSelection(stop));
    setShowPaymentModal(true);
    loadStopPhotos(stop.id);
  };

  // Open payment modal to edit existing payment status
  const editPaymentStatus = (stop) => {
    setPendingPaymentStop(stop);
    setIsEditingPayment(true);

    // Pre-populate discount if exists, otherwise reset
    if (stop.discount_amount && stop.discount_type) {
      setStopDiscount({
        amount: stop.discount_amount.toString(),
        type: stop.discount_type,
        reason: stop.discount_reason || '',
        ongoing: false
      });
      setShowDiscountForm(true);
    } else {
      setStopDiscount({ amount: '', type: '', reason: '', ongoing: false });
      setShowDiscountForm(false);
    }

    // Pre-populate extra charge if exists, otherwise reset
    if (stop.extra_charge_amount && parseFloat(stop.extra_charge_amount) > 0) {
      setExtraCharge({
        amount: stop.extra_charge_amount.toString(),
        reason: stop.extra_charge_reason || ''
      });
      setShowExtraChargeForm(true);
    } else {
      setExtraCharge({ amount: '', reason: '' });
      setShowExtraChargeForm(false);
    }

    // Pre-populate salt toggle for as_needed customers
    if (stop.salt_preference === 'as_needed') {
      setSaltAppliedToggle(stop.salt_applied === true);
    }

    // Pre-populate comp status if exists
    setIsComp(stop.is_comp === true);
    setCompReason(stop.comp_reason || '');

    // Pre-populate selected add-ons from existing stop data
    setSelectedAddons(stopAddonSelection(stop));

    setShowPaymentModal(true);
    loadStopPhotos(stop.id);
  };

  // Load photos for a stop
  const loadStopPhotos = async (stopId) => {
    photoStopIdRef.current = stopId;
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/route-stops/${stopId}/photos`);
      setStopPhotos(response.data);
    } catch (err) {
      console.error('Failed to load photos:', err);
      setStopPhotos([]);
    }
  };

  // Handle photo upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
    if (!file || !pendingPaymentStop) return;

    setPhotoPreparing(true);
    let compressedFile;
    try {
      compressedFile = await compressImage(file);
    } catch (err) {
      console.error('Photo could not be read:', err);
      showAlert('That photo could not be read. Please take it again.', 'Error');
      return;
    } finally {
      setPhotoPreparing(false);
    }

    const pending = {
      key: `${Date.now()}-${Math.random()}`,
      stopId: pendingPaymentStop.id,
      file: compressedFile,
      previewUrl: URL.createObjectURL(compressedFile),
      status: 'uploading',
    };
    setPendingPhotos(prev => [...prev, pending]);
    uploadPendingPhoto(pending);
  };

  // Upload, or re-upload after a failure. On failure the compressed photo stays
  // on screen with a Retry, so a weak signal in a driveway does not mean
  // taking it again.
  const uploadPendingPhoto = async (pending) => {
    setPendingPhotos(prev => prev.map(p => (p.key === pending.key ? { ...p, status: 'uploading' } : p)));
    try {
      const formData = new FormData();
      formData.append('photo', pending.file);
      formData.append('photoType', 'other');

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/route-stops/${pending.stopId}/photos`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      URL.revokeObjectURL(pending.previewUrl);
      setPendingPhotos(prev => prev.filter(p => p.key !== pending.key));
      if (photoStopIdRef.current === pending.stopId) {
        setStopPhotos(prev => [response.data, ...prev]);
      }
    } catch (err) {
      console.error('Photo upload failed:', err);
      setPendingPhotos(prev => prev.map(p => (p.key === pending.key ? { ...p, status: 'failed' } : p)));
    }
  };

  // Compress image before upload. Rejects rather than hanging when the file
  // cannot be read or decoded.
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('file could not be read'));
      reader.onload = (e) => {
        const img = new Image();
        img.onerror = () => reject(new Error('image could not be decoded'));
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxSize = 1200;
          let width = img.width;
          let height = img.height;

          if (width > height && width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          } else if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            if (!blob) {
              reject(new Error('image could not be compressed'));
              return;
            }
            resolve(new File([blob], file.name, { type: 'image/jpeg' }));
          }, 'image/jpeg', 0.8);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // Delete a photo
  const handleDeletePhoto = async (photoId) => {
    showConfirm('Delete this photo?', async () => {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/api/photos/${photoId}`);
        setStopPhotos(prev => prev.filter(p => p.id !== photoId));
      } catch (err) {
        console.error('Failed to delete photo:', err);
        showAlert('Failed to delete photo.', 'Error');
      }
    }, { title: 'Delete Photo', confirmText: 'Delete', confirmColor: '#dc2626' });
  };


  // Handle saving adjustments only (discount, extra charge, salt, comp) without changing payment
  // Used when editing a completed stop and only wanting to update adjustments
  const handleSaveAdjustments = async () => {
    if (!pendingPaymentStop) return;

    const stop = pendingPaymentStop;
    setShowPaymentModal(false);
    setStopPhotos([]);
    setSelectedPhoto(null);
    setCompleting(stop.id);

    try {
      const editPayload = {};

      // Handle discount - can be added, updated, or cleared
      if (stopDiscount.type && stopDiscount.amount) {
        editPayload.discountAmount = parseFloat(stopDiscount.amount);
        editPayload.discountType = stopDiscount.type;
        editPayload.discountReason = stopDiscount.reason || null;
      } else {
        // Clear discount if form is empty
        editPayload.discountAmount = null;
        editPayload.discountType = null;
        editPayload.discountReason = null;
      }

      // Handle extra charge - can be added, updated, or cleared
      if (extraCharge.amount && parseFloat(extraCharge.amount) > 0) {
        editPayload.extraChargeAmount = parseFloat(extraCharge.amount);
        editPayload.extraChargeReason = extraCharge.reason || null;
      } else {
        // Clear extra charge if form is empty
        editPayload.extraChargeAmount = null;
        editPayload.extraChargeReason = null;
      }

      // Handle salt for as_needed customers
      if (stop.salt_preference === 'as_needed') {
        editPayload.saltApplied = saltAppliedToggle;
      }

      // Handle comp status - can be added, updated, or cleared
      editPayload.isComp = isComp;
      editPayload.compReason = isComp ? (compReason || null) : null;

      // Service add-ons: sent only when the ticks changed, and empty when
      // every add-on was unticked.
      const editAddons = stopAddonsPayload(availableAddons, selectedAddons, stop);
      if (editAddons !== undefined) {
        editPayload.addons = editAddons;
      }

      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/stops/${stop.id}`,
        editPayload
      );

      // Reload route
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
      );
      setSelectedRoute(response.data);

      // Reload referral discounts
      if (response.data.storm_id) {
        await loadReferralDiscounts(response.data);
      }
    } catch (err) {
      if (handleLapsedError(err)) return;
      console.error('Save adjustments error:', err);
      showAlert('Failed to save changes', 'Error');
    } finally {
      setCompleting(null);
      setPendingPaymentStop(null);
      setAmountCollected('');
      setIsEditingPayment(false);
      setShowDiscountForm(false);
      setShowExtraChargeForm(false);
      setExtraCharge({ amount: '', reason: '' });
      setIsComp(false);
      setCompReason('');
    }
  };

  // Handle payment selection and complete the stop (or update existing payment)
  // method: a payment method id from the company's list, 'comp', or null.
  // status: 'paid' or 'not_paid'. Two arguments, so no method id is ever
  // read apart by a '_paid' suffix.
  const handlePaymentSelection = async (method, status) => {
    if (!pendingPaymentStop) return;

    const stop = pendingPaymentStop;

    let paymentStatus;
    let actualPaymentMethod = null;

    if (method === 'comp') {
      paymentStatus = 'paid'; // Comp is considered "paid" (no balance due)
      actualPaymentMethod = 'comp';
    } else if (!method) {
      paymentStatus = 'not_paid';
      actualPaymentMethod = null;
    } else if (isSquare(method)) {
      paymentStatus = 'unpaid'; // Square customers get invoiced later via Bill All
      // On a stop awaiting depth this is "Square, invoice after depth": the
      // method is recorded, not paid and with no amount, so the auto-invoice
      // after depth and Bill All pick it up once the depth prices it.
      if (stopAwaitsDepth(stop)) paymentStatus = 'not_paid';
      actualPaymentMethod = 'square';
    } else {
      paymentStatus = status === 'paid' ? 'paid' : 'not_paid';
      actualPaymentMethod = method;
    }

    // A stop awaiting the storm depth has no price, so a paid choice must carry
    // the amount collected (design 3.4). Checked before anything is sent.
    const awaitingDepth = stopAwaitsDepth(stop) && method !== 'comp';
    const doorPaid = awaitingDepth && paymentStatus === 'paid';
    const doorAmount = parseFloat(amountCollected);
    if (doorPaid && !(doorAmount > 0)) {
      setDoorPaymentError('This stop is waiting for the storm depth, so enter the amount you collected before marking it paid.');
      return;
    }
    setDoorPaymentError('');
    // Recorded below through /payment, once the stop is saved as not paid.
    if (doorPaid) paymentStatus = 'not_paid';

    setShowPaymentModal(false);
    setStopPhotos([]);
    setSelectedPhoto(null);
    setCompleting(stop.id);

    try {
      if (isEditingPayment) {
        // Update payment status, discount, extra charge, and salt for completed stop
        const editPayload = {
          paymentStatus: paymentStatus,
          paymentMethod: actualPaymentMethod,
        };

        // Handle discount - can be added, updated, or cleared
        if (stopDiscount.type && stopDiscount.amount) {
          editPayload.discountAmount = parseFloat(stopDiscount.amount);
          editPayload.discountType = stopDiscount.type;
          editPayload.discountReason = stopDiscount.reason || null;
        } else {
          // Clear discount if form is empty
          editPayload.discountAmount = null;
          editPayload.discountType = null;
          editPayload.discountReason = null;
        }

        // Handle extra charge - can be added, updated, or cleared
        if (extraCharge.amount && parseFloat(extraCharge.amount) > 0) {
          editPayload.extraChargeAmount = parseFloat(extraCharge.amount);
          editPayload.extraChargeReason = extraCharge.reason || null;
        } else {
          // Clear extra charge if form is empty
          editPayload.extraChargeAmount = null;
          editPayload.extraChargeReason = null;
        }

        // Handle salt for as_needed customers
        if (stop.salt_preference === 'as_needed') {
          editPayload.saltApplied = saltAppliedToggle;
        }

        // Handle comp status - can be added, updated, or cleared
        editPayload.isComp = isComp;
        editPayload.compReason = isComp ? (compReason || null) : null;

        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/stops/${stop.id}`,
          editPayload
        );
      } else {
        // Determine saltApplied value based on customer preference
        // For "as_needed" customers, use the toggle; for others, derive from preference
        let saltAppliedValue = null; // null means use customer default
        if (stop.salt_preference === 'as_needed') {
          saltAppliedValue = saltAppliedToggle;
        } else if (stop.salt_preference === 'always') {
          saltAppliedValue = true;
        } else if (stop.salt_preference === 'never') {
          saltAppliedValue = false;
        }

        // Build the stop update payload with discount and extra charge if provided
        const stopUpdatePayload = {
          status: 'completed',
          completedAt: new Date().toISOString(),
          paymentStatus: paymentStatus,
          paymentMethod: actualPaymentMethod,
          saltApplied: saltAppliedValue,
        };

        // Add discount fields if a discount was applied
        if (stopDiscount.type && stopDiscount.amount) {
          stopUpdatePayload.discountAmount = parseFloat(stopDiscount.amount);
          stopUpdatePayload.discountType = stopDiscount.type;
          stopUpdatePayload.discountReason = stopDiscount.reason || null;
          stopUpdatePayload.applyOngoing = stopDiscount.ongoing;
        }

        // Add extra charge fields if an extra charge was applied
        if (extraCharge.amount && parseFloat(extraCharge.amount) > 0) {
          stopUpdatePayload.extraChargeAmount = parseFloat(extraCharge.amount);
          stopUpdatePayload.extraChargeReason = extraCharge.reason || null;
        }

        // Add comp fields if marked as comp
        if (isComp) {
          stopUpdatePayload.isComp = true;
          stopUpdatePayload.compReason = compReason || null;
        }

        // Service add-ons: sent only when the ticks changed from the ones the
        // stop picked up, and empty when every add-on was unticked.
        const completionAddons = stopAddonsPayload(availableAddons, selectedAddons, stop);
        if (completionAddons !== undefined) {
          stopUpdatePayload.addons = completionAddons;
        }

        // New completion - set status and log service
        const stopUpdateResponse = await axios.put(
          `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/stops/${stop.id}`,
          stopUpdatePayload
        );

        // The stops alerted ahead after this completion (migration 108).
        mergeArrivalIntoRoute(selectedRoute.id, stopUpdateResponse.data?.arrival_window);

        // Check for auto-invoice result and show notification
        if (stopUpdateResponse.data?.autoInvoice) {
          const autoInvoice = stopUpdateResponse.data.autoInvoice;
          if (autoInvoice.success) {
            // Use setTimeout to show alert after modal closes
            setTimeout(() => {
              showAlert(`Invoice sent to ${autoInvoice.customerName}`, 'Success');
            }, 100);
          } else if (autoInvoice.skipped && autoInvoice.code === 'DEPTH_REQUIRED') {
            // Square, invoice after depth: nothing failed. The invoice goes
            // out after the depth is entered.
          } else if (autoInvoice.error) {
            setTimeout(() => {
              showAlert(`Auto-invoice failed: ${autoInvoice.error}`, 'Error');
            }, 100);
          }
        }

        // Get calculated price (with salt and extra charge if applicable)
        // Comp stops are logged with $0 price
        const basePrice = isComp ? 0 : (parseFloat(stop.price_snow) || 0);
        const saltPrice = isComp ? 0 : (saltAppliedValue ? (parseFloat(stop.salt_price) || 0) : 0);
        const extraChargeAmt = isComp ? 0 : ((extraCharge.amount && parseFloat(extraCharge.amount) > 0) ? parseFloat(extraCharge.amount) : 0);
        // Display only: a linked service is priced by the stop's lock.
        const addonsTotal = isComp ? 0 : addonsAmount(availableAddons, selectedAddons, stop, basePrice + saltPrice);
        let calculatedPrice = basePrice + saltPrice + extraChargeAmt + addonsTotal;
        if (!isComp && stopDiscount.type && stopDiscount.amount) {
          calculatedPrice = calculateDiscountedPrice(basePrice + saltPrice + extraChargeAmt + addonsTotal, parseFloat(stopDiscount.amount), stopDiscount.type);
        }

        // Log the service (only for new completions)
        // Price includes salt, extra charge, and add-ons if applied
        await axios.post(`${process.env.REACT_APP_API_URL}/api/services`, {
          customerId: stop.customer_id,
          routeId: selectedRoute.id,
          routeStopId: stop.id,
          serviceType: selectedRoute.service_type || 'snow_removal',
          serviceDate: companyToday(timezone),
          price: basePrice + saltPrice + extraChargeAmt + addonsTotal,
          calculatedPrice: calculatedPrice,
        });
      }

      // The door payment on a stop awaiting depth: the stop is saved, now mark
      // it paid with the amount collected. A refusal leaves it not paid.
      if (doorPaid) {
        try {
          await axios.put(`${process.env.REACT_APP_API_URL}/api/route-stops/${stop.id}/payment`, {
            paymentStatus: 'paid',
            paymentMethod: actualPaymentMethod,
            amountPaid: doorAmount,
          });
        } catch (payErr) {
          const msg = doorPaymentErrorMessage(payErr) || payErr?.response?.data?.error || 'The payment could not be recorded.';
          setTimeout(() => showAlert(`${msg} The stop was saved as not paid.`, 'Payment not recorded'), 100);
        }
      }

      // Reload route. Arm the depth auto-open before the route state changes,
      // so the effect sees the reloaded route.
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
      );
      if (!isEditingPayment) depthAutoOpenArmed.current = true;
      setSelectedRoute(response.data);

      // Reload referral discounts after completion (other customers may now have more completed referrals)
      if (!isEditingPayment && response.data.storm_id) {
        await loadReferralDiscounts(response.data);
      }

      // Check if all stops are completed (only relevant for new completions)
      if (!isEditingPayment) {
        const allCompleted = response.data.stops?.every(s => s.status === 'completed' || s.status === 'skipped');
        if (allCompleted && response.data.stops?.length > 0) {
          // Send only the status. Spreading the fetched record here carried the
          // same snake_case/camelCase mismatch as the route start above, so
          // finishing a route nulled service_type, storm_id and driver pay a
          // second time.
          await axios.put(`${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`, {
            status: 'completed',
          });
          loadRoutes();
        }
      }

      // The first completed stop of a trial starts the storm clock. Pick it up
      // now so the Dashboard banner shows when the free storm ends.
      if (!isEditingPayment && isOnTrial() && !selectedRoute.storm_event_counted) {
        refreshUser({ silent: true });
      }
    } catch (err) {
      if (handleLapsedError(err)) return;
      console.error('Failed to update stop:', err);
      showAlert(isEditingPayment ? 'Failed to update payment status' : 'Failed to mark stop as complete', 'Error');
    } finally {
      setCompleting(null);
      setPendingPaymentStop(null);
      setAmountCollected('');
      setIsEditingPayment(false);
      // Reset discount and extra charge forms
      setShowDiscountForm(false);
      setStopDiscount({ amount: '', type: '', reason: '', ongoing: false });
      setShowExtraChargeForm(false);
      setExtraCharge({ amount: '', reason: '' });
      // Reset comp status
      setIsComp(false);
      setCompReason('');
    }
  };

  const markStopSkipped = async (stop) => {
    setCompleting(stop.id);
    try {
      const skipResponse = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/stops/${stop.id}`,
        {
          status: 'skipped',
          completedAt: new Date().toISOString(),
          notes: 'Skipped',
        }
      );
      mergeArrivalIntoRoute(selectedRoute.id, skipResponse.data?.arrival_window);
      depthAutoOpenArmed.current = true;

      // Reload route
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
      );
      setSelectedRoute(response.data);

      // Reload referral discounts to reflect the skip
      if (response.data.storm_id) {
        await loadReferralDiscounts(response.data);
      }
    } catch (err) {
      if (handleLapsedError(err)) return;
      console.error('Failed to skip stop:', err);
      showAlert('Failed to skip stop', 'Error');
    } finally {
      setCompleting(null);
    }
  };

  // Revert a skipped stop back to completed (for admins editing completed routes)
  const revertSkippedToCompleted = async (stop) => {
    setCompleting(stop.id);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/stops/${stop.id}`,
        {
          status: 'completed',
          paymentStatus: 'not_paid',
          paymentMethod: null
        }
      );

      // Reload route
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
      );
      setSelectedRoute(response.data);

      // Reload referral discounts to reflect the change
      if (response.data.storm_id) {
        await loadReferralDiscounts(response.data);
      }
    } catch (err) {
      if (handleLapsedError(err)) return;
      console.error('Failed to revert stop:', err);
      showAlert('Failed to revert stop', 'Error');
    } finally {
      setCompleting(null);
    }
  };

  // Mark a completed stop as skipped (from the payment modal)
  const markCompletedAsSkipped = async () => {
    if (!pendingPaymentStop) return;

    setShowPaymentModal(false);
    setStopPhotos([]);
    setSelectedPhoto(null);
    setCompleting(pendingPaymentStop.id);

    try {
      const skipResponse = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/stops/${pendingPaymentStop.id}`,
        {
          status: 'skipped',
          paymentStatus: null,
          paymentMethod: null,
          notes: 'Skipped'
        }
      );
      mergeArrivalIntoRoute(selectedRoute.id, skipResponse.data?.arrival_window);
      depthAutoOpenArmed.current = true;

      // Reload route
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
      );
      setSelectedRoute(response.data);

      // Reload referral discounts
      if (response.data.storm_id) {
        await loadReferralDiscounts(response.data);
      }
    } catch (err) {
      if (handleLapsedError(err)) return;
      console.error('Failed to mark as skipped:', err);
      showAlert('Failed to mark as skipped', 'Error');
    } finally {
      setCompleting(null);
      setPendingPaymentStop(null);
      setAmountCollected('');
      setIsEditingPayment(false);
    }
  };

  const resetRoute = async () => {
    showConfirm('Reset all stops to pending? This will clear completion status.', async () => {
      try {
        // Reset all stops
        for (const stop of selectedRoute.stops) {
          await axios.put(
            `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/stops/${stop.id}`,
            {
              status: 'pending',
              completedAt: null,
            }
          );
        }

        // Reset route status. Only the status is sent, as for start and finish:
        // the fetched record is snake_case and the route update reads camelCase.
        await axios.put(`${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`, {
          status: 'not_started',
        });

        // Reload
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
        );
        setSelectedRoute(response.data);
        loadRoutes();
        showAlert('Route reset - all stops set to pending', 'Success');
      } catch (err) {
        if (handleLapsedError(err)) return;
        console.error('Failed to reset route:', err);
        showAlert('Failed to reset route', 'Error');
      }
    }, { title: 'Reset Route', confirmText: 'Reset', confirmColor: '#dc2626' });
  };

  // Archive or unarchive a route
  const toggleArchiveRoute = async () => {
    const isArchived = selectedRoute.is_archived;
    const action = isArchived ? 'unarchive' : 'archive';

    showConfirm(`${isArchived ? 'Unarchive' : 'Archive'} this route?`, async () => {
      try {
        // isArchived, the key the route update reads. The spread record's
        // is_archived was never read, so this button archived nothing.
        await axios.put(`${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`, {
          isArchived: !isArchived,
        });

        setSelectedRoute(null);
        loadRoutes();
        showAlert(`Route ${action}d successfully`, 'Success');
      } catch (err) {
        console.error(`Failed to ${action} route:`, err);
        showAlert(`Failed to ${action} route`, 'Error');
      }
    }, { title: `${isArchived ? 'Unarchive' : 'Archive'} Route`, confirmText: isArchived ? 'Unarchive' : 'Archive', confirmColor: isArchived ? '#1e88e5' : '#dc2626' });
  };

  const getProgress = () => {
    if (!selectedRoute?.stops?.length) return { completed: 0, total: 0, percentage: 0 };
    const completed = selectedRoute.stops.filter(s => s.status === 'completed' || s.status === 'skipped').length;
    const total = selectedRoute.stops.length;
    return {
      completed,
      total,
      percentage: Math.round((completed / total) * 100),
    };
  };

  const getPendingStops = () => {
    return selectedRoute?.stops?.filter(s => s.status === 'pending') || [];
  };

  const getCompletedStops = () => {
    return selectedRoute?.stops?.filter(s => s.status === 'completed' || s.status === 'skipped') || [];
  };

  const getServicesText = (stop) => {
    const services = [];
    if (stop.driveway) services.push('Driveway');
    if (stop.sidewalks) services.push('Sidewalks');
    if (stop.walkways) services.push('Walkways');
    if (stop.salt) services.push('Salt');
    return services.length > 0 ? services.join(', ') : 'Standard service';
  };

  // Helper to get payment method display name
  const getPaymentMethodName = (method) => methodLabel(paymentMethods, method);

  // Helper to get combined payment status label (e.g., "Venmo - Not Paid", "Square - Invoice Sent")
  const getPaymentLabel = (stop) => {
    const methodName = getPaymentMethodName(stop.payment_method);
    if (stop.payment_status === 'paid') {
      return methodName ? `${methodName} - Paid` : 'Paid';
    } else if (stop.payment_status === 'invoice_sent') {
      // Square invoice sent - keep special terminology
      return methodName ? `${methodName} - Invoice Sent` : 'Invoice Sent';
    } else if (stop.payment_method && stop.payment_status !== 'paid') {
      // Other methods with not_paid status
      return `${methodName} - Not Paid`;
    }
    return 'Not Paid';
  };

  // Helper to calculate discounted price
  const calculateDiscountedPrice = (price, discountAmount, discountType) => {
    if (!discountAmount || discountAmount <= 0) return price;
    if (discountType === 'percent') {
      return Math.max(0, price - (price * discountAmount / 100));
    }
    return Math.max(0, price - discountAmount);
  };

  // Determine if salt should be included for a stop
  const shouldIncludeSalt = (stop) => {
    // If salt_applied is explicitly set on the stop, use that
    if (stop.salt_applied === true) return true;
    if (stop.salt_applied === false) return false;
    // Otherwise fall back to customer's default salt setting
    return stop.salt === true;
  };

  // Price lock (070). What a COMPLETED stop displays, from the stored lock
  // only: original is the locked subtotal, charged the locked total, and the
  // discount shown is the stop's own or the customer discount snapshotted at
  // completion, never the customer's current one. Null for a stop that is not
  // completed (it keeps the live preview below), and for a completed stop the
  // server returned no lock for. priced is false for a stop completed with no
  // price set.
  const lockedDisplay = (stop) => {
    if (!stop || stop.status !== 'completed') return null;
    if (isUnpricedLockedStop(stop)) return { priced: false, original: 0, charged: 0, discount: null };
    const charged = lockedStopTotal(stop);
    if (charged === null) return null;
    const sub = lockedStopSubtotal(stop);
    const discount = stop.discount_amount && stop.discount_type
      ? { amount: stop.discount_amount, type: stop.discount_type, reason: stop.discount_reason }
      : stop.locked_customer_discount_amount && stop.locked_customer_discount_type
      ? { amount: stop.locked_customer_discount_amount, type: stop.locked_customer_discount_type, reason: null }
      : null;
    return { priced: true, original: sub === null ? charged : sub, charged, discount };
  };

  // Get effective price for a stop (considering extra charge, stop-level or customer-level discount)
  const getEffectivePrice = (stop) => {
    // Price lock (070): a completed stop's figure is the server's stored
    // locked_total, never computed here. An unpriced completed stop counts 0
    // for arithmetic only; every display of it says "No price set". A stop
    // awaiting depth has no figure at all; the route sums leave it out.
    if (isPendingDepthStop(stop)) return 0;
    const locked = lockedDisplay(stop);
    if (locked) return locked.charged;
    const includeSalt = shouldIncludeSalt(stop);
    const extraCharge = parseFloat(stop.extra_charge_amount || 0);
    // A stop with no price contributes 0, which is what the server's price
    // engine does with a NULL price_snow (services/pricing.js reads it as
    // base:missing). This used to read 50, so the route total on the driver's
    // screen was $50 higher per unpriced stop than the invoice the server
    // produced for the same work. The 0 is for arithmetic only: every place that
    // DISPLAYS a per-stop price now says "No price set" instead of showing it.
    const basePrice = (stopTotal(stop, includeSalt) ?? 0) + extraCharge;
    // Check stop-level discount first
    if (stop.discount_amount && stop.discount_type) {
      return calculateDiscountedPrice(basePrice, parseFloat(stop.discount_amount), stop.discount_type);
    }
    // Then check customer-level discount
    if (stop.customer_discount_amount && stop.customer_discount_type) {
      return calculateDiscountedPrice(basePrice, parseFloat(stop.customer_discount_amount), stop.customer_discount_type);
    }
    return basePrice;
  };

  // Route sums over completed stops. A stop awaiting depth has no figure, so
  // it is never summed (not even as $0): it is counted and shown as
  // "+ N awaiting depth" next to the priced total.
  const sumCompleted = (filter) => {
    const stops = (selectedRoute?.stops || []).filter(s => s.status === 'completed' && filter(s));
    const priced = stops.filter(s => !isPendingDepthStop(s));
    return {
      total: priced.reduce((sum, stop) => sum + getEffectivePrice(stop), 0),
      pending: stops.length - priced.length,
      pricedCount: priced.length,
    };
  };
  const formatRouteAmount = (sum) => {
    if (sum.pending > 0 && sum.pricedCount === 0) return AWAITING_DEPTH_LABEL;
    if (sum.pending > 0) return `${formatCurrency(sum.total)} + ${sum.pending} awaiting depth`;
    return formatCurrency(sum.total);
  };

  const routeTotalSum = () => sumCompleted(() => true);
  const paidTotalSum = () => sumCompleted(s => s.payment_status === 'paid');
  const unpaidTotalSum = () => sumCompleted(s => s.payment_status !== 'paid');

  // Print header (design 5 row 14): "Depth: 6.5 in (NWS ...)" or "Depth: not
  // entered", from GET /api/routes/:routeId/fees, else the route itself.
  const printDepthHeader = () => {
    const inches = routeFees?.snowDepthInches ?? selectedRoute?.snow_depth_inches;
    if (inches === null || inches === undefined || inches === '') return 'not entered';
    const source = routeFees?.depthSource || selectedRoute?.depth_source;
    return `${parseFloat(inches).toFixed(1)} in${source ? ` (${source})` : ''}`;
  };

  const handlePrint = () => {
    // Fetch processing fees for print summary
    if (selectedRoute?.id) {
      axios.get(`${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/fees`)
        .then(res => setRouteFees(res.data))
        .catch(() => setRouteFees(null));
    }
    setShowPrintView(true);
    setTimeout(() => {
      window.print();
    }, 100);
  };

  // Tell the server the driver is heading to this stop (SMS Phase 5). The
  // server sends the arrival alert once per stop, by text or email, when the
  // company has it on, and answers with the stop's alert state plus window[],
  // the stops alerted ahead of it at Next 2 or Next 3 stops (migration 108).
  // All of it is merged into the route so the indicators show without a
  // reload. A stop already alerted is still told: its window may have stops
  // left to alert. Never throws: the alert must never get in the driver's way.
  const sendOnMyWay = async (stop) => {
    if (!selectedRoute || !stop) return;
    if (stop.status === 'completed' || stop.status === 'skipped') return;
    if (arrivalSending[stop.id]) return;
    const routeId = selectedRoute.id;
    const show = Boolean(selectedRoute.arrival_alerts_enabled) && !stop.arrival_alert_sent_at;
    if (show) setArrivalSending(prev => ({ ...prev, [stop.id]: true }));
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/route-stops/${stop.id}/on-my-way`
      );
      mergeArrivalIntoRoute(routeId, tapArrivalEntries(stop.id, data));
    } catch (err) {
      console.error('On my way failed:', err.response?.status || err.message);
    } finally {
      if (show) setArrivalSending(prev => { const next = { ...prev }; delete next[stop.id]; return next; });
    }
  };

  // Merges arrival states into the selected route, if it is still routeId.
  const mergeArrivalIntoRoute = (routeId, entries) => {
    setSelectedRoute(prev => {
      if (!prev || prev.id !== routeId) return prev;
      const stops = mergeArrivalStates(prev.stops, entries);
      return stops === prev.stops ? prev : { ...prev, stops };
    });
  };

  const renderArrivalIndicator = (stop) => {
    const ind = arrivalIndicator(stop, Boolean(arrivalSending[stop.id]), timezone);
    // The skip notice (migration 114), only ever on a skipped stop.
    const skip = skipIndicator(stop, timezone);
    if (!ind && !skip) return null;
    return (
      <>
        {ind && <div className={`arrival-indicator arrival-${ind.tone}`}>{ind.text}</div>}
        {skip && <div className={`arrival-indicator arrival-${skip.tone}`}>{skip.text}</div>}
      </>
    );
  };

  // Open Google Maps with directions to the stop address. The maps tab opens
  // first and regardless; the arrival alert goes out alongside it.
  const openNavigation = (stop) => {
    const address = encodeURIComponent(
      `${stop.address}, ${stop.city}, ${stop.state} ${stop.zip}`
    );
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${address}`, '_blank');
    sendOnMyWay(stop);
  };

  // Load available customers (not already on this route)
  const loadAvailableCustomers = async () => {
    setRouteRequests(null);
    if (selectedRoute?.id) loadRouteRequests(selectedRoute.id).then(setRouteRequests);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/customers`);
      // Filter out customers already on this route
      const currentCustomerIds = selectedRoute?.stops?.map(s => s.customer_id) || [];
      const available = response.data.filter(c => !currentCustomerIds.includes(c.id));
      setAvailableCustomers(available);
    } catch (err) {
      console.error('Failed to load customers:', err);
    }
  };

  // Open add customer modal
  const openAddCustomerModal = () => {
    loadAvailableCustomers();
    setCustomerSearch('');
    setShowAddCustomer(true);
  };

  // Add customer to route at optimal position
  const addCustomerMidRoute = async (customer) => {
    // An on-call customer who has not asked for this day: ask first. The
    // server notes the stop "Added without request".
    if (!confirmNoRequest(customer.request)) return;
    setAddingCustomer(true);

    // Do Not Serve: the server answers 409 DO_NOT_SERVE until the request
    // carries confirmDoNotServe. The question is asked at most once per add,
    // whichever POST meets the 409 first, and the answer is carried to any
    // POST that follows. A no is thrown as doNotServeDeclined so the catch
    // below returns BEFORE its fallback re-POST instead of treating the
    // refusal as a failure and trying again.
    let doNotServeConfirmed = false;
    const postStop = async (stopOrder) => {
      const url = `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/stops`;
      const body = { customerId: customer.id, stopOrder, notes: 'Added mid-route' };
      try {
        await axios.post(url, doNotServeConfirmed ? { ...body, confirmDoNotServe: true } : body);
      } catch (err) {
        if (!isDoNotServeConflict(err) || doNotServeConfirmed) throw err;
        if (!confirmDoNotServe(err)) {
          const declined = new Error('Do Not Serve add declined');
          declined.doNotServeDeclined = true;
          throw declined;
        }
        doNotServeConfirmed = true;
        await axios.post(url, { ...body, confirmDoNotServe: true });
      }
    };

    try {
      // Find optimal insert position using Google Maps
      const insertResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/find-insert-position`,
        {
          customerId: customer.id
        }
      );

      const { insertPosition } = insertResponse.data;

      // Add the stop at the calculated position
      await postStop(insertPosition);

      // Shift stop orders for stops after the insertion point
      const pendingStops = selectedRoute.stops.filter(s => s.status === 'pending');
      for (const stop of pendingStops) {
        if (stop.stop_order >= insertPosition) {
          await axios.put(
            `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/stops/${stop.id}`,
            { stopOrder: stop.stop_order + 1 }
          );
        }
      }

      // Reload route
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
      );
      setSelectedRoute(response.data);
      setShowAddCustomer(false);
      showAlert(`${customer.first_name} ${customer.last_name} added to route at position ${insertPosition}!`, 'Success');
    } catch (err) {
      // The owner said no to a Do Not Serve customer: not a failure, and not
      // a reason to try the fallback.
      if (err.doNotServeDeclined) return;
      // Lapsed: the fallback would be refused the same way.
      if (handleLapsedError(err)) return;
      console.error('Failed to add customer:', err);
      // Fallback: add to end of route
      try {
        const maxOrder = Math.max(...(selectedRoute.stops?.map(s => s.stop_order) || [0]), 0);
        await postStop(maxOrder + 1);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
        );
        setSelectedRoute(response.data);
        setShowAddCustomer(false);
        showAlert(`${customer.first_name} ${customer.last_name} added to end of route.`, 'Success');
      } catch (fallbackErr) {
        if (fallbackErr.doNotServeDeclined) return;
        showAlert('Failed to add customer to route', 'Error');
      }
    } finally {
      setAddingCustomer(false);
    }
  };

  // Handle tier change - auto-fill prices from dynamic pricing tiers
  const handleTierChange = (snowTierId) => {
    let newPriceSnow = newCustomer.priceSnow;
    let newSaltPrice = newCustomer.saltPrice;

    if (snowTierId !== 'custom') {
      // Find the selected tier from pricingTiers
      const selectedTier = pricingTiers.find(t => String(t.id) === snowTierId);
      if (selectedTier) {
        newPriceSnow = parseFloat(selectedTier.default_price).toFixed(2);
        newSaltPrice = parseFloat(selectedTier.salt_price || 0).toFixed(2);
      }
    }
    // For 'custom', keep the current prices

    setNewCustomer({
      ...newCustomer,
      snowTierId,
      priceSnow: newPriceSnow,
      saltPrice: newSaltPrice,
    });
  };

  // Quick-add new customer and add to route
  const createAndAddCustomer = async () => {
    // Validate required fields
    if (!newCustomer.firstName || !newCustomer.address || !newCustomer.city || !newCustomer.state || !newCustomer.zip) {
      showAlert('Please fill in Name, Address, City, State, and Zip');
      return;
    }

    setAddingCustomer(true);
    try {
      // Create the customer
      const customerResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/customers`,
        {
          firstName: newCustomer.firstName,
          lastName: newCustomer.lastName || '',
          address: newCustomer.address,
          city: newCustomer.city,
          state: newCustomer.state,
          zip: newCustomer.zip,
          phone: newCustomer.phone || '',
          email: newCustomer.email || '',
          snowTierId: newCustomer.snowTierId === 'custom' ? null : parseInt(newCustomer.snowTierId, 10),
          // LEFT AS IS, DELIBERATELY. Sending null here would change nothing:
          // POST /api/customers binds `priceSnow || 50.00` server-side, so a
          // blank price becomes 50.00 whatever this line sends. Making the
          // client send null would only look like it did something.
          //
          // This is the last $50 default in the product, and it sits on an
          // owner-driven create form where somebody is present and typing,
          // rather than on the public intake where a stranger's silence used to
          // be turned into a price. Removing it is a separate decision about
          // manual customer creation, not part of the size-confirmation work.
          priceSnow: parseFloat(newCustomer.priceSnow) || 50.00,
          saltPrice: parseFloat(newCustomer.saltPrice) || 8.00,
          driveway: newCustomer.driveway,
          sidewalks: newCustomer.sidewalks,
          walkways: newCustomer.walkways,
          salt: newCustomer.salt,
          paymentMethodPreferred: newCustomer.paymentMethodPreferred,
          serviceFrequency: newCustomer.serviceFrequency,
          notes: newCustomer.notes || '',
          leadSource: newCustomer.leadSource || null,
          serviceType: 'snow_removal',
          isActive: true
        }
      );

      const createdCustomer = customerResponse.data;

      // Now add to route using the same logic as addCustomerMidRoute
      try {
        const insertResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/find-insert-position`,
          { customerId: createdCustomer.id }
        );

        const { insertPosition } = insertResponse.data;

        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/stops`,
          {
            customerId: createdCustomer.id,
            stopOrder: insertPosition,
            notes: 'Storm call - new customer'
          }
        );

        // Shift stop orders for stops after the insertion point
        const pendingStops = selectedRoute.stops.filter(s => s.status === 'pending');
        for (const stop of pendingStops) {
          if (stop.stop_order >= insertPosition) {
            await axios.put(
              `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/stops/${stop.id}`,
              { stopOrder: stop.stop_order + 1 }
            );
          }
        }

        // Reload route
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
        );
        setSelectedRoute(response.data);
        setShowAddCustomer(false);
        setShowNewCustomerForm(false);
        setNewCustomer({
          firstName: '', lastName: '', address: '', city: '', state: '', zip: '', phone: '', email: '',
          priceSnow: '35.00', driveway: true, sidewalks: false, walkways: false, salt: false,
          paymentMethodPreferred: 'square', serviceFrequency: 'seasonal', notes: ''
        });
        showAlert(`New customer ${createdCustomer.first_name} ${createdCustomer.last_name} created and added at position ${insertPosition}!`, 'Success');
      } catch (routeErr) {
        // Fallback: add to end of route
        const maxOrder = Math.max(...(selectedRoute.stops?.map(s => s.stop_order) || [0]), 0);
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/stops`,
          {
            customerId: createdCustomer.id,
            stopOrder: maxOrder + 1,
            notes: 'Storm call - new customer'
          }
        );
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
        );
        setSelectedRoute(response.data);
        setShowAddCustomer(false);
        setShowNewCustomerForm(false);
        setNewCustomer({
          firstName: '', lastName: '', address: '', city: '', state: '', zip: '', phone: '', email: '',
          priceSnow: '35.00', driveway: true, sidewalks: false, walkways: false, salt: false,
          paymentMethodPreferred: 'square', serviceFrequency: 'seasonal', notes: ''
        });
        showAlert(`New customer ${createdCustomer.first_name} ${createdCustomer.last_name} created and added to end of route.`, 'Success');
      }
    } catch (err) {
      if (handleLapsedError(err)) return;
      console.error('Failed to create customer:', err);
      showAlert(err.response?.data?.error || 'Failed to create customer', 'Error');
    } finally {
      setAddingCustomer(false);
    }
  };

  // Filter customers by search
  // Filtered by search, then on-call customers who asked for this day first
  // and those who did not greyed at the end (utils/serviceRequests.js). The
  // list is root customers, so each is its own root.
  const filteredCustomers = withRequestFlags(routeRequests, availableCustomers.filter(c => {
    if (!customerSearch) return true;
    const search = customerSearch.toLowerCase();
    return (
      c.first_name?.toLowerCase().includes(search) ||
      c.last_name?.toLowerCase().includes(search) ||
      c.address?.toLowerCase().includes(search) ||
      c.city?.toLowerCase().includes(search)
    );
  }).map(c => ({ ...c, servicePreference: c.service_preference, rootId: c.id })));

  // Helper to get current GPS position as a Promise
  const getCurrentGpsPosition = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lon: position.coords.longitude,
          });
        },
        (error) => {
          reject(error);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    });
  };

  // Re-optimize remaining stops from current position
  const reoptimizeRemaining = async () => {
    if (getPendingStops().length < 2) {
      showAlert('Need at least 2 pending stops to optimize');
      return;
    }

    setReoptimizing(true);
    setGpsStatus('');

    try {
      let requestData = {};

      // If GPS optimization is enabled, try to get current location
      if (useGpsForOptimization) {
        setGpsStatus('acquiring');
        try {
          const position = await getCurrentGpsPosition();
          requestData = {
            currentLat: position.lat,
            currentLon: position.lon,
            useGps: true,
          };
          setGpsStatus('success');
        } catch (gpsError) {
          console.warn('GPS unavailable, using last stop:', gpsError.message);
          setGpsStatus('unavailable');
          requestData = { useGps: false };
        }
      } else {
        setGpsStatus('');
        requestData = { useGps: false };
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/optimize-remaining`,
        requestData
      );

      if (response.data.optimized) {
        // Reload route to get new order
        const routeResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
        );
        setSelectedRoute(routeResponse.data);
        const locationMsg = response.data.usedGps
          ? 'your current GPS location'
          : 'the last completed stop';
        showAlert(`Route re-optimized from ${locationMsg}! ${response.data.stopsReordered} stops reordered.`, 'Success');
      } else {
        showAlert(response.data.message || 'Route not optimized');
      }
    } catch (err) {
      console.error('Failed to re-optimize:', err);
      showAlert(err.response?.data?.error || 'Failed to re-optimize route', 'Error');
    } finally {
      setReoptimizing(false);
      // Clear GPS status after a delay
      setTimeout(() => setGpsStatus(''), 3000);
    }
  };

  // Get current location info for display
  const getCurrentLocation = () => {
    const completedStops = getCompletedStops();
    if (completedStops.length > 0) {
      // Sort by completed_at descending to get the most recent
      const sorted = [...completedStops].sort((a, b) =>
        new Date(b.completed_at) - new Date(a.completed_at)
      );
      const lastStop = sorted[0];
      return {
        type: 'stop',
        name: `${lastStop.first_name} ${lastStop.last_name}`,
        address: lastStop.address,
      };
    }
    if (selectedRoute?.starting_address) {
      return {
        type: 'start',
        name: 'Starting Address',
        address: selectedRoute.starting_address,
      };
    }
    return null;
  };

  const progress = getProgress();

  return (
    <div className="storm-mode-page">
      {/* Trial Expired Modal */}
      {showTrialModal && (
        <TrialExpired
          variant="modal"
          onClose={() => setShowTrialModal(false)}
        />
      )}
      {/* Header */}
      <StaffHeader active="storm-mode" className="storm-header" />

      <PullToRefreshIndicator />

      <div className="page-container storm-container">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
          </div>
        ) : !selectedRoute ? (
          /* Route Selection */
          <div className="route-selection">
            <h1 className="storm-title">Storm Mode</h1>
            <p className="storm-subtitle">Select a route to begin servicing</p>

            {/* Route Filter Tabs and Sort Toggle */}
            <div className="route-controls">
              <div className="route-filter-tabs">
                <button
                  className={`filter-tab ${routeFilter === 'active' ? 'active' : ''}`}
                  onClick={() => setRouteFilter('active')}
                >
                  Active ({routes.filter(r => r.status !== 'completed' && !r.is_archived).length})
                </button>
                <button
                  className={`filter-tab ${routeFilter === 'completed' ? 'active' : ''}`}
                  onClick={() => setRouteFilter('completed')}
                >
                  Completed ({routes.filter(r => r.status === 'completed' && !r.is_archived).length})
                </button>
                <button
                  className={`filter-tab ${routeFilter === 'archived' ? 'active' : ''}`}
                  onClick={() => setRouteFilter('archived')}
                >
                  Archived ({routes.filter(r => r.is_archived).length})
                </button>
              </div>
              <button className="sort-toggle" onClick={toggleRouteSortOrder}>
                {routeSortOrder === 'newest' ? '↓ Newest' : '↑ Oldest'}
              </button>
            </div>

            {routes.length === 0 ? (
              <div className="empty-state">
                <p>No routes available</p>
                <button className="btn btn-primary" onClick={() => navigate('/routes')}>
                  Create a Route
                </button>
              </div>
            ) : (
              <div className="route-select-grid">
                {routes
                  .filter(route => {
                    if (routeFilter === 'active') return route.status !== 'completed' && !route.is_archived;
                    if (routeFilter === 'completed') return route.status === 'completed' && !route.is_archived;
                    if (routeFilter === 'archived') return route.is_archived;
                    return true;
                  })
                  .sort((a, b) => {
                    const dateA = new Date(a.created_at || 0);
                    const dateB = new Date(b.created_at || 0);
                    return routeSortOrder === 'newest' ? dateB - dateA : dateA - dateB;
                  })
                  .map((route) => (
                  <button
                    key={route.id}
                    className={`route-select-card ${route.status === 'completed' ? 'completed' : ''} ${route.is_archived ? 'archived' : ''}`}
                    onClick={() => handleRouteClick(route)}
                  >
                    <div className="route-select-name">{route.name}</div>
                    <div className="route-select-meta">
                      {route.status === 'in_progress' && (
                        <span className="status-badge in-progress">In Progress</span>
                      )}
                      {route.status === 'completed' && (
                        <span className="status-badge completed">Completed</span>
                      )}
                      {route.status !== 'in_progress' && route.status !== 'completed' && (
                        <span className="status-badge not-started">Not Started</span>
                      )}
                      {route.is_archived && (
                        <span className="status-badge archived">Archived</span>
                      )}
                      <span>{route.stop_count || 0} stops</span>
                      {!shouldHidePrices() && (
                        <span className="route-total">{formatCurrency(route.total_price)}</span>
                      )}
                      {isDriver() && route.driver_pay_type && route.driver_pay_rate && (
                        <span className="driver-pay-badge">
                          Your pay: {formatCurrency(route.driver_pay_rate)}{route.driver_pay_type === 'hourly' ? '/hr' : ''}
                        </span>
                      )}
                      {isAdmin() && (
                        route.assigned_to_name ? (
                          <span className="assigned-badge">{route.assigned_to_name}</span>
                        ) : (
                          <span className="assigned-badge unassigned">Unassigned</span>
                        )
                      )}
                      {!shouldHidePrices() && route.status === 'completed' && parseFloat(route.total_price || 0) > 0 && (
                        <span className="payment-breakdown">
                          {parseFloat(route.total_unpaid || 0) === 0 ? (
                            <span className="payment-fully-paid">✓ Paid</span>
                          ) : parseFloat(route.total_paid || 0) === 0 ? (
                            <span className="payment-unpaid">{formatCurrency(route.total_unpaid)} unpaid</span>
                          ) : (
                            <>
                              <span className="payment-paid">{formatCurrency(route.total_paid)}</span>
                              <span className="payment-separator">|</span>
                              <span className="payment-unpaid">{formatCurrency(route.total_unpaid)}</span>
                            </>
                          )}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
                {routes.filter(route => {
                  if (routeFilter === 'active') return route.status !== 'completed' && !route.is_archived;
                  if (routeFilter === 'completed') return route.status === 'completed' && !route.is_archived;
                  if (routeFilter === 'archived') return route.is_archived;
                  return true;
                }).length === 0 && (
                  <div className="empty-filter-state">
                    <p>No {routeFilter} routes</p>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Active Route View */
          <div className="active-route">
            {/* Route Header */}
            <div className="route-header">
              <div className="route-header-top">
                <button className="btn btn-outline btn-back" onClick={() => setSelectedRoute(null)}>
                  Back
                </button>
                <h1 className="route-title">{selectedRoute.name}</h1>
              </div>
              <div className="route-header-meta">
                <span className="progress-info">
                  {progress.completed} / {progress.total} stops
                </span>
                {selectedRoute.status === 'completed' && (
                  <span className="route-status-badge completed">Completed</span>
                )}
                {selectedRoute.is_archived && (
                  <span className="route-status-badge archived">Archived</span>
                )}
                {isAdmin() && (
                  selectedRoute.assigned_to_name ? (
                    <span className="assigned-badge">{selectedRoute.assigned_to_name}</span>
                  ) : (
                    <span className="assigned-badge unassigned">Unassigned</span>
                  )
                )}
              </div>
              <div className="route-header-actions">
                {selectedRoute.status !== 'completed' && !selectedRoute.is_archived && (
                  <button className="btn btn-primary btn-sm" onClick={openAddCustomerModal}>
                    + Add Stop
                  </button>
                )}
                {selectedRoute.status === 'completed' && (
                  <button className="btn btn-outline btn-sm" onClick={() => {
                    if (selectedRoute?.id) {
                      axios.get(`${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/fees`)
                        .then(res => setRouteFees(res.data))
                        .catch(() => setRouteFees(null));
                    }
                    setShowPrintView(true);
                  }}>
                    Print Summary
                  </button>
                )}
                {isAdmin() && (
                  <button className="btn btn-outline btn-sm" onClick={toggleArchiveRoute}>
                    {selectedRoute.is_archived ? 'Unarchive' : 'Archive'}
                  </button>
                )}
                {selectedRoute.status !== 'completed' && !selectedRoute.is_archived && (
                  <button className="btn btn-outline btn-sm" onClick={resetRoute}>
                    Reset
                  </button>
                )}
              </div>
            </div>

            {/* Progress Bar */}
            <div className="progress-bar-container">
              <div
                className="progress-bar-fill"
                style={{ width: `${progress.percentage}%` }}
              ></div>
            </div>

            {/* Stops awaiting the storm depth (depth pricing 3.3) */}
          {isDepthRoute(selectedRoute) && pendingDepthCount > 0 && (
            <div className="depth-pending-banner" role="status">
              <span>
                {pendingDepthCount} completed stop{pendingDepthCount === 1 ? ' is' : 's are'} waiting for a measured depth.
              </span>
              {isAdmin() && (
                <button className="btn btn-sm depth-pending-btn" onClick={() => setShowDepthModal(true)}>
                  Enter depth
                </button>
              )}
            </div>
          )}

          {/* Current Location & Re-optimize */}
            {getCurrentLocation() && (
              <div className="current-location-bar">
                <div className="current-location-info">
                  <span className="current-location-label">Current Location:</span>
                  <span className="current-location-name">{getCurrentLocation().name}</span>
                  <span className="current-location-address">({getCurrentLocation().address})</span>
                </div>
                {getPendingStops().length >= 2 && (
                  <div className="reoptimize-container">
                    {gpsStatus && (
                      <span className={`gps-status gps-status-${gpsStatus}`}>
                        {gpsStatus === 'acquiring' && '📍 Acquiring GPS...'}
                        {gpsStatus === 'success' && '📍 Using GPS location'}
                        {gpsStatus === 'unavailable' && '⚠️ GPS unavailable, using last stop'}
                      </span>
                    )}
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={reoptimizeRemaining}
                      disabled={reoptimizing}
                    >
                      {reoptimizing ? 'Optimizing...' : 'Re-optimize Remaining'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Stops List */}
            {getPendingStops().length === 0 && getCompletedStops().length > 0 ? (
              /* Completed Route View - show all stops with details */
              <div className="completed-route-view">
                <div className="route-complete-banner">
                  <span className="complete-check">✓</span>
                  <span>Route Complete - {getCompletedStops().filter(s => s.status === 'completed').length} completed
                  {getCompletedStops().filter(s => s.status === 'skipped').length > 0 &&
                    `, ${getCompletedStops().filter(s => s.status === 'skipped').length} skipped`}
                  </span>
                  {!shouldHidePrices() && (
                    <span className="route-total-banner">Total: {formatRouteAmount(routeTotalSum())}</span>
                  )}
                </div>

                {!shouldHidePrices() && (
                  <div className="payment-summary-bar">
                    <div className="payment-summary-item paid">
                      <span className="payment-summary-label">Paid</span>
                      <span className="payment-summary-amount">{formatRouteAmount(paidTotalSum())}</span>
                    </div>
                    <div className="payment-summary-divider"></div>
                    <div className="payment-summary-item unpaid">
                      <span className="payment-summary-label">Unpaid</span>
                      <span className="payment-summary-amount">{formatRouteAmount(unpaidTotalSum())}</span>
                    </div>
                    <div className="payment-summary-divider"></div>
                    <div className="payment-summary-item total">
                      <span className="payment-summary-label">Total</span>
                      <span className="payment-summary-amount">{formatRouteAmount(routeTotalSum())}</span>
                    </div>
                  </div>
                )}

                <div className="complete-actions-bar">
                  {!isDriver() && (
                    <button className="btn btn-secondary" onClick={handlePrint}>
                      Print Summary
                    </button>
                  )}
                  {!isDriver() && (isPro() || isSuperAdmin()) && (
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        setBillingResults(null);
                        setSelectedBillStops(new Set());
                        setShowBillingModal(true);
                      }}
                    >
                      Bill All
                    </button>
                  )}
                  <button className="btn btn-outline" onClick={() => setSelectedRoute(null)}>
                    Return to Routes
                  </button>
                </div>

                {/* All Completed/Skipped Stops */}
                <div className="stops-section">
                  <h2 className="stops-section-title">
                    All Stops ({getCompletedStops().length})
                  </h2>
                  <div className="completed-stops-detail-list">
                    {getCompletedStops().map((stop, index) => (
                      <div
                        key={stop.id}
                        className={`completed-stop-card ${stop.status === 'skipped' ? 'skipped' : ''} ${stop.status === 'skipped' && isAdmin() ? 'admin-clickable' : ''}`}
                        tabIndex={0}
                        role="button"
                        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.click(); }}
                        onClick={() => {
                          if (stop.status === 'skipped') {
                            if (isAdmin()) {
                              showConfirm('Revert this stop from Skipped to Completed?', () => {
                                revertSkippedToCompleted(stop);
                              }, { title: 'Revert Stop', confirmText: 'Revert' });
                            }
                          } else {
                            editPaymentStatus(stop);
                          }
                        }}
                      >
                        <div className="stop-order-badge">{index + 1}</div>
                        <div className="stop-details">
                          <div className="stop-customer-name">
                            {stop.first_name} {stop.last_name}
                            {stop.status === 'skipped' && <span className="skipped-label">Skipped{isAdmin() && ' (tap to revert)'}</span>}
                          </div>
                          <div className="stop-address">{stop.address}</div>
                          <div className="stop-city">{stop.city}, {stop.state} {stop.zip}</div>
                          {stop.phone && (
                            <button
                              className="stop-phone"
                              onClick={e => handlePhoneClick(e, stop.phone)}
                            >
                              {formatPhoneDisplay(stop.phone)}
                            </button>
                          )}
                          {!shouldHidePrices() && stop.status !== 'skipped' && (() => {
                            const extraChargeAmt = parseFloat(stop.extra_charge_amount || 0);
                            // Price lock (070): a completed stop shows its stored figures.
                            const locked = lockedDisplay(stop);
                            const priced = locked ? locked.priced : hasStopPrice(stop);
                            const basePrice = stopTotal(stop, shouldIncludeSalt(stop)) ?? 0;
                            const basePriceWithExtra = locked ? locked.original : basePrice + extraChargeAmt;
                            const hasStopDiscount = stop.discount_amount && stop.discount_type;
                            const hasCustomerDiscount = stop.customer_discount_amount && stop.customer_discount_type;
                            const hasReferralDiscount = referralDiscounts[stop.customer_id] && referralDiscounts[stop.customer_id].discount > 0;
                            const hasAnyDiscount = locked
                              ? locked.original !== locked.charged
                              : (hasStopDiscount || hasCustomerDiscount || hasReferralDiscount);
                            const effectivePrice = getEffectivePrice(stop);
                            return (
                            <div className="stop-price">
                              {/* No price on the customer means no price on the
                                  stop, and no discount pair either: a percentage
                                  off nothing is not information. The driver sees
                                  that this stop is unpriced, which is the truth
                                  the server will also act on when it bills 0. */}
                              {isPendingDepthStop(stop) ? (
                                <span className="regular-price awaiting-depth-label">{AWAITING_DEPTH_LABEL}</span>
                              ) : !priced ? (
                                <span className="regular-price price-unset">{NO_PRICE_LABEL}</span>
                              ) : hasAnyDiscount ? (
                                <>
                                  <span className="original-price">${basePriceWithExtra.toFixed(2)}</span>
                                  <span className="discounted-price">${effectivePrice.toFixed(2)}</span>
                                </>
                              ) : (
                                <span className="regular-price">
                                  ${basePriceWithExtra.toFixed(2)}
                                </span>
                              )}
                              {stop.salt && <span className="salt-badge">Salt</span>}
                              {stopDepthBand(stop) && !isPendingDepthStop(stop) && (
                                <span className="depth-band-chip">{stopDepthBand(stop)}</span>
                              )}
                              {extraChargeAmt > 0 && (
                                <span className="extra-charge-badge" title={stop.extra_charge_reason || 'Extra charge'}>
                                  +${extraChargeAmt.toFixed(2)}
                                </span>
                              )}
                              {stop.payment_method_preferred && (
                                <span className={`payment-method-badge ${stop.payment_method_preferred}`}>
                                  {getPaymentMethodName(stop.payment_method_preferred)}
                                </span>
                              )}
                            </div>
                          );})()}
                          {stop.notes && (
                            <div className="stop-notes">{stop.notes}</div>
                          )}
                        </div>
                        {!shouldHidePrices() && (
                          <div className="stop-payment-status">
                            {stop.status === 'skipped' ? (
                              <span className="payment-badge skipped">Skipped</span>
                            ) : isSquare(stop.payment_method) ? (
                              /* Square customers get special badge based on invoice/payment state */
                              stop.payment_status === 'paid' ? (
                                <span className="square-badge paid">Square - Paid</span>
                              ) : stop.invoice_sent_at ? (
                                <span className="square-badge invoice-sent">Invoice Sent</span>
                              ) : (
                                <span className="square-badge pending">Square</span>
                              )
                            ) : (
                              /* Non-Square payment methods */
                              <span className={`payment-badge ${
                                stop.payment_status === 'paid' ? 'paid' : 'not-paid'
                              }`}>
                                {getPaymentLabel(stop)}
                              </span>
                            )}
                            {stop.invoice_stale === true && isAdmin() && (isPro() || isSuperAdmin()) && (
                              <button
                                type="button"
                                className="btn btn-sm bill-diff-btn"
                                disabled={billingDiffStopId === stop.id}
                                onClick={(e) => { e.stopPropagation(); handleBillDifference(stop); }}
                              >
                                {billingDiffStopId === stop.id ? 'Sending...' : 'Bill the difference'}
                              </button>
                            )}
                            {diffResults[stop.id] && (
                              <span className={`bill-diff-result ${diffResults[stop.id].ok ? 'ok' : 'err'}`}>{diffResults[stop.id].message}</span>
                            )}
                            {stop.status !== 'skipped' && (
                              <span className="tap-to-edit">Tap to edit</span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Pending Stops */}
                <div className="stops-section">
                  <h2 className="stops-section-title">
                    Up Next ({getPendingStops().length})
                  </h2>
                  <div className="storm-stops-list">
                    {getPendingStops().map((stop, index) => (
                      <div
                        key={stop.id}
                        className={`storm-stop-card ${index === 0 ? 'next-up' : ''}`}
                      >
                        <div className="stop-order-badge">{index + 1}</div>
                        <div className="stop-details">
                          <div className="stop-customer-name">
                            {stop.first_name} {stop.last_name}
                            <span className={`service-frequency-badge ${preferenceBadge(stop.root_service_preference).className}`}>
                              {preferenceBadge(stop.root_service_preference).label}
                            </span>
                            {/* Salt preference - visible to all including drivers */}
                            {stop.salt_preference && (
                              <span className={`salt-pref-badge ${stop.salt_preference}`}>
                                {/* The stored values are always, as_needed and
                                    never. 'ask' was never written by anything,
                                    so an as-needed stop used to show an empty
                                    badge. */}
                                {stop.salt_preference === 'always' ? 'Salt: Yes' :
                                 stop.salt_preference === 'never' ? 'Salt: No' :
                                 stop.salt_preference === 'as_needed' ? 'Salt: As needed' : ''}
                              </span>
                            )}
                            {/* Payment method preference - visible to all including drivers */}
                            {stop.payment_method_preferred && (
                              <span className={`payment-method-badge ${stop.payment_method_preferred}`}>
                                {getPaymentMethodName(stop.payment_method_preferred)}
                              </span>
                            )}
                          </div>
                          <div className="stop-address">{stop.address}</div>
                          <div className="stop-city">{stop.city}, {stop.state} {stop.zip}</div>
                          {/* The customer's add-ons, "Done by: 6:30 am" (104). */}
                          {customerAddonLines(stop).length > 0 && (
                            <div className="stop-addon-lines">{customerAddonLines(stop).join(' · ')}</div>
                          )}
                          {stop.phone && (
                            <button
                              className="stop-phone"
                              onClick={e => handlePhoneClick(e, stop.phone)}
                            >
                              {formatPhoneDisplay(stop.phone)}
                            </button>
                          )}
                          {!shouldHidePrices() && (
                            <div className="stop-price">
                              {/* Unpriced first, for the same reason as the
                                  pending-stop card above: none of the discount
                                  branches below can say anything true about a
                                  price nobody has set. A depth route's stop
                                  shows its price by depth instead: the range
                                  while the route has no depth, the band total
                                  once it has one (design 2.5). */}
                              {depthPreviewFor(stop) ? (
                                <>
                                  <span className={`regular-price${depthPreviewFor(stop).kind === 'range' || depthPreviewFor(stop).kind === 'band' ? ' depth-preview-price' : ' price-unset'}`}>
                                    {formatDepthPreview(depthPreviewFor(stop))}
                                  </span>
                                  {shouldIncludeSalt(stop) && <span className="salt-badge">Salt</span>}
                                </>
                              ) : depthPreviewLoading ? (
                                <span className="regular-price price-unset">By depth</span>
                              ) : !hasStopPrice(stop) ? (
                                <>
                                  <span className="regular-price price-unset">{NO_PRICE_LABEL}</span>
                                  {shouldIncludeSalt(stop) && <span className="salt-badge">Salt</span>}
                                </>
                              ) : stop.customer_discount_amount && stop.customer_discount_type ? (
                                <>
                                  <span className="original-price">
                                    {formatCurrency(stopTotal(stop, shouldIncludeSalt(stop)))}
                                  </span>
                                  <span className="discounted-price">
                                    ${getEffectivePrice(stop).toFixed(2)}
                                  </span>
                                  {shouldIncludeSalt(stop) && <span className="salt-badge">Salt</span>}
                                  <span className="discount-badge">
                                    {stop.customer_discount_type === 'percent'
                                      ? `${stop.customer_discount_amount}% off`
                                      : `$${parseFloat(stop.customer_discount_amount).toFixed(2)} off`}
                                  </span>
                                </>
                              ) : referralDiscounts[stop.customer_id] && referralDiscounts[stop.customer_id].expectedDiscount > 0 ? (
                                <>
                                  <span className="original-price">${referralDiscounts[stop.customer_id].basePrice.toFixed(2)}</span>
                                  <span className="discounted-price">${referralDiscounts[stop.customer_id].expectedPrice.toFixed(2)}</span>
                                  {referralDiscounts[stop.customer_id].saltIncluded && (
                                    <span className="salt-badge">Salt</span>
                                  )}
                                  {/* Payment method badge shown at top for all users */}
                                  <span className="referral-discount-badge">
                                    {referralDiscounts[stop.customer_id].totalReferrals} referral{referralDiscounts[stop.customer_id].totalReferrals !== 1 ? 's' : ''} (-${referralDiscounts[stop.customer_id].expectedDiscount})
                                  </span>
                                </>
                              ) : (
                                <>
                                  <span className="regular-price">
                                    {formatCurrency(stopTotal(stop, shouldIncludeSalt(stop)))}
                                  </span>
                                  {shouldIncludeSalt(stop) && <span className="salt-badge">Salt</span>}
                                  {/* Payment method badge shown at top for all users */}
                                </>
                              )}
                            </div>
                          )}
                          {!shouldHidePrices() && stop.customer_discount_reason && (
                            <div className="stop-discount-reason">{stop.customer_discount_reason}</div>
                          )}
                          {stop.notes && (
                            <div className="stop-notes">{stop.notes}</div>
                          )}
                          {renderArrivalIndicator(stop)}
                        </div>
                        <div className="stop-actions">
                          <button
                            className="btn btn-lg btn-success stop-complete-btn"
                            onClick={() => markStopComplete(stop)}
                            disabled={completing === stop.id}
                          >
                            {completing === stop.id ? '...' : 'Done'}
                          </button>
                          <div className="stop-secondary-actions">
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => openNavigation(stop)}
                            >
                              Navigate
                            </button>
                            {canSendArrival(selectedRoute, stop) && (
                              <button
                                className="btn btn-sm btn-outline"
                                onClick={() => sendOnMyWay(stop)}
                                disabled={Boolean(arrivalSending[stop.id])}
                              >
                                On my way
                              </button>
                            )}
                            <button
                              className="btn btn-sm btn-outline"
                              onClick={() => markStopSkipped(stop)}
                              disabled={completing === stop.id}
                            >
                              Skip
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Completed Stops */}
                {getCompletedStops().length > 0 && (
                  <div className="stops-section completed-section">
                    <h2 className="stops-section-title">
                      Completed ({getCompletedStops().length})
                    </h2>
                    <div className="completed-stops-list">
                      {getCompletedStops().map((stop) => {
                        const extraChargeAmt = parseFloat(stop.extra_charge_amount || 0);
                        // Price lock (070): a completed stop shows its stored figures,
                        // and the discount snapshotted with them.
                        const locked = lockedDisplay(stop);
                        const priced = locked ? locked.priced : hasStopPrice(stop);
                        const basePrice = stopTotal(stop, shouldIncludeSalt(stop)) ?? 0;
                        const totalPrice = locked ? locked.original : basePrice + extraChargeAmt;
                        const hasDiscount = locked
                          ? locked.original !== locked.charged
                          : (stop.discount_amount && stop.discount_type) || (stop.customer_discount_amount && stop.customer_discount_type);
                        const effectivePrice = getEffectivePrice(stop);
                        // Get discount info from stop-level first, then customer-level
                        const discountInfo = locked ? locked.discount : stop.discount_amount && stop.discount_type
                          ? { amount: stop.discount_amount, type: stop.discount_type, reason: stop.discount_reason }
                          : stop.customer_discount_amount && stop.customer_discount_type
                          ? { amount: stop.customer_discount_amount, type: stop.customer_discount_type, reason: stop.customer_discount_reason }
                          : null;
                        return (
                          <div
                            key={stop.id}
                            className={`completed-stop-item ${stop.status !== 'skipped' ? 'editable' : ''} ${stop.status === 'skipped' && isAdmin() ? 'admin-clickable' : ''} ${hasDiscount ? 'has-discount' : ''}`}
                            onClick={() => {
                              if (stop.status === 'skipped') {
                                if (isAdmin()) {
                                  showConfirm('Revert this stop from Skipped to Completed?', () => {
                                    revertSkippedToCompleted(stop);
                                  }, { title: 'Revert Stop', confirmText: 'Revert' });
                                }
                              } else {
                                editPaymentStatus(stop);
                              }
                            }}
                          >
                            <span className={`completed-icon ${stop.status === 'skipped' ? 'skipped' : ''}`}>
                              {stop.status === 'skipped' ? '—' : '✓'}
                            </span>
                            <div className="completed-info">
                              <span className="completed-name">
                                {stop.first_name} {stop.last_name}
                                {!shouldHidePrices() && extraChargeAmt > 0 && (
                                  <span className="extra-charge-indicator" title={stop.extra_charge_reason || 'Extra charge'}>
                                    +${extraChargeAmt.toFixed(2)}
                                  </span>
                                )}
                              </span>
                              {!shouldHidePrices() && hasDiscount && discountInfo && (
                                <span className="completed-discount-info">
                                  {discountInfo.type === 'percent'
                                    ? `${discountInfo.amount}% off`
                                    : `$${parseFloat(discountInfo.amount).toFixed(2)} off`}
                                  {discountInfo.reason && ` - ${discountInfo.reason}`}
                                </span>
                              )}
                              {renderArrivalIndicator(stop)}
                            </div>
                            {!shouldHidePrices() && (
                              <span className="completed-price">
                                {stop.is_comp ? (
                                  <span className="comp-badge">COMP</span>
                                ) : isPendingDepthStop(stop) ? (
                                  <span className="awaiting-depth-label">{AWAITING_DEPTH_LABEL}</span>
                                ) : !priced ? (
                                  /* Completed, and still unpriced. Worth showing
                                     plainly: this is work already done that will
                                     bill 0 until somebody sets a price. */
                                  <span className="price-unset">{NO_PRICE_LABEL}</span>
                                ) : hasDiscount ? (
                                  <>
                                    <span className="price-strike">${totalPrice.toFixed(2)}</span>
                                    <span className="price-discounted">${effectivePrice.toFixed(2)}</span>
                                  </>
                                ) : (
                                  `$${totalPrice.toFixed(2)}`
                                )}
                                {stopDepthBand(stop) && !isPendingDepthStop(stop) && !stop.is_comp && (
                                  <span className="depth-band-chip">{stopDepthBand(stop)}</span>
                                )}
                              </span>
                            )}
                            {!shouldHidePrices() && (stop.status === 'skipped' ? (
                              <span className="payment-badge skipped">Skipped</span>
                            ) : isSquare(stop.payment_method) ? (
                              /* Square customers get special badge based on invoice/payment state */
                              stop.payment_status === 'paid' ? (
                                <span className="square-badge paid">Square - Paid</span>
                              ) : stop.invoice_sent_at ? (
                                <span className="square-badge invoice-sent">Invoice Sent</span>
                              ) : (
                                <span className="square-badge pending">Square</span>
                              )
                            ) : (
                              /* Non-Square payment methods */
                              <span className={`payment-badge ${
                                stop.payment_status === 'paid' ? 'paid' : 'not-paid'
                              }`}>
                                {getPaymentLabel(stop)}
                              </span>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Print View */}
      {showPrintView && (
        <div className="print-view" ref={printRef}>
          <div className="print-header">
            <h1>{getAppName()} - Route Summary</h1>
            <div className="print-meta">
              <p><strong>Date:</strong> {new Date().toLocaleDateString('en-US', inZone(timezone, {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }))}</p>
              <p><strong>Route:</strong> {selectedRoute?.name}</p>
              {isDepthRoute(selectedRoute) && (
                <p><strong>Depth:</strong> {printDepthHeader()}</p>
              )}
            </div>
          </div>

          <table className="print-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Customer</th>
                <th>Address</th>
                <th>Services</th>
                <th className="text-right">Price</th>
                <th>Payment</th>
              </tr>
            </thead>
            <tbody>
              {selectedRoute?.stops?.filter(s => s.status === 'completed').map((stop, index) => {
                // Price lock (070): every row here is completed, so the stored figures.
                const locked = lockedDisplay(stop);
                const priced = locked ? locked.priced : hasStopPrice(stop);
                const basePrice = locked ? locked.original : stopTotal(stop, shouldIncludeSalt(stop)) ?? 0;
                const hasDiscount = locked
                  ? locked.original !== locked.charged
                  : (stop.discount_amount && stop.discount_type) || (stop.customer_discount_amount && stop.customer_discount_type);
                const finalPrice = getEffectivePrice(stop);
                const discountInfo = locked ? locked.discount : stop.discount_amount && stop.discount_type
                  ? { amount: stop.discount_amount, type: stop.discount_type, reason: stop.discount_reason }
                  : stop.customer_discount_amount && stop.customer_discount_type
                  ? { amount: stop.customer_discount_amount, type: stop.customer_discount_type, reason: stop.customer_discount_reason }
                  : null;
                const paymentLabel = getPaymentLabel(stop);
                return (
                  <tr key={stop.id}>
                    <td>{index + 1}</td>
                    <td>
                      {stop.first_name} {stop.last_name}
                      {discountInfo && discountInfo.reason && (
                        <div style={{fontSize: '0.75rem', color: 'var(--color-success, #059669)', fontStyle: 'italic'}}>
                          {discountInfo.reason}
                        </div>
                      )}
                    </td>
                    <td>{stop.address}, {stop.city}</td>
                    <td>
                      {getServicesText(stop)}
                      {stopDepthBand(stop) && (
                        <div style={{fontSize: '0.75rem'}}>{snowServiceLabel(stopDepthBand(stop))}</div>
                      )}
                      {stopPremiumPercent(stop) && !isPendingDepthStop(stop) && (
                        <div style={{fontSize: '0.75rem'}}>{premiumLineLabel(stopPremiumPercent(stop))}</div>
                      )}
                    </td>
                    <td className="text-right">
                      {isPendingDepthStop(stop) ? (
                        <span className="awaiting-depth-label">{AWAITING_DEPTH_LABEL}</span>
                      ) : !priced ? (
                        <span className="price-unset">{NO_PRICE_LABEL}</span>
                      ) : hasDiscount ? (
                        <span>
                          <span style={{textDecoration: 'line-through', color: 'var(--color-text-light)', marginRight: '8px'}}>
                            ${basePrice.toFixed(2)}
                          </span>
                          <span style={{color: 'var(--color-success, #059669)', fontWeight: '600'}}>
                            ${finalPrice.toFixed(2)}
                          </span>
                          <div style={{fontSize: '0.7rem', color: 'var(--color-success, #059669)'}}>
                            {discountInfo?.type === 'percent' ? `${discountInfo.amount}% off` : `$${parseFloat(discountInfo?.amount || 0).toFixed(2)} off`}
                          </div>
                        </span>
                      ) : (
                        `$${finalPrice.toFixed(2)}`
                      )}
                    </td>
                    <td className={`payment-status ${stop.payment_status || 'not-paid'}`}>{paymentLabel}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="5" className="text-right"><strong>Route Revenue:</strong></td>
                <td className="text-right"><strong>{formatRouteAmount(routeTotalSum())}</strong></td>
              </tr>
            </tfoot>
          </table>

          {/* Route Expenses Section */}
          {routeExpenses.length > 0 && (
            <div className="print-expenses-section">
              <h3>Route Expenses</h3>
              <table className="print-table expenses-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Category</th>
                    <th>Description</th>
                    <th className="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {routeExpenses.map((expense) => (
                    <tr key={expense.id}>
                      <td>{expense.date || 'N/A'}</td>
                      <td style={{ textTransform: 'capitalize' }}>{expense.category?.replace('_', ' ')}</td>
                      <td>{expense.description || '-'}</td>
                      <td className="text-right" style={{ color: 'var(--color-error)' }}>-{formatCurrency(expense.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="3" className="text-right"><strong>Total Expenses:</strong></td>
                    <td className="text-right" style={{ color: 'var(--color-error)' }}>
                      <strong>-{formatCurrency(routeExpenses.reduce((sum, e) => sum + parseFloat(e.amount), 0))}</strong>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Net Profit Summary */}
          <div className="print-profit-summary">
            <div className="profit-row">
              <span>Gross Revenue:</span>
              <span className="profit-amount">{formatRouteAmount(routeTotalSum())}</span>
            </div>
            {routeFees && routeFees.totalFees > 0 && (
              <>
                <div className="profit-row">
                  <span>Processing Fees:</span>
                  <span className="profit-amount" style={{ color: 'var(--color-error)' }}>
                    -{formatCurrency(routeFees.totalFees)}
                  </span>
                </div>
                {Object.entries(routeFees.feesByMethod || {}).map(([method, data]) => (
                  <div key={method} className="profit-row" style={{ paddingLeft: '20px', fontSize: '0.85em', color: 'var(--color-text-light)' }}>
                    <span>{getPaymentMethodName(method)} ({data.count} payment{data.count !== 1 ? 's' : ''}):</span>
                    <span>-{formatCurrency(data.totalFees)}</span>
                  </div>
                ))}
              </>
            )}
            {routeExpenses.length > 0 && (
              <div className="profit-row">
                <span>Total Expenses:</span>
                <span className="profit-amount" style={{ color: 'var(--color-error)' }}>
                  -{formatCurrency(routeExpenses.reduce((sum, e) => sum + parseFloat(e.amount), 0))}
                </span>
              </div>
            )}
            <div className="profit-row net">
              <span><strong>Net Total:</strong></span>
              <span className="profit-amount">
                <strong>{(() => {
                  // Stops awaiting depth stay out of the net, as in the
                  // gross above: an all-pending route with nothing to
                  // deduct reads Awaiting depth, never $0.00.
                  const sum = routeTotalSum();
                  const deductions = (routeFees?.totalFees || 0)
                    + routeExpenses.reduce((total, e) => total + parseFloat(e.amount), 0);
                  if (sum.pending > 0 && sum.pricedCount === 0 && deductions === 0) return AWAITING_DEPTH_LABEL;
                  const net = formatCurrency(sum.total - deductions);
                  return sum.pending > 0 ? `${net} + ${sum.pending} awaiting depth` : net;
                })()}</strong>
              </span>
            </div>
          </div>

          <div className="print-footer">
            <p>Stops completed: {selectedRoute?.stops?.filter(s => s.status === 'completed').length}</p>
            <p>Paid: {selectedRoute?.stops?.filter(s => s.status === 'completed' && s.payment_status === 'paid').length} |
               Unpaid: {selectedRoute?.stops?.filter(s => s.status === 'completed' && s.payment_status !== 'paid').length}</p>
            {pendingDepthCount > 0 && (
              <p>Awaiting depth: {pendingDepthCount} stop{pendingDepthCount !== 1 ? 's' : ''}, not included in the totals above.</p>
            )}
          </div>

          <button className="print-close-btn no-print" onClick={() => setShowPrintView(false)} aria-label="Close print preview">
            Close Preview
          </button>
        </div>
      )}

      {/* Start Route Modal */}
      {showStartRouteModal && pendingRouteToStart && (
        <div className="modal-overlay" onClick={() => { setShowStartRouteModal(false); setPendingRouteToStart(null); }}>
          <div className="start-route-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="start-route-modal-header">
              <h2>Start Route</h2>
              <p className="start-route-name">{pendingRouteToStart.name}</p>
            </div>

            <div className="start-route-modal-body">
              <p className="start-route-question">Where are you starting from?</p>

              {/* Saved Location Option */}
              <label className={`start-location-option ${startLocationOption === 'saved' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="startLocation"
                  value="saved"
                  checked={startLocationOption === 'saved'}
                  onChange={() => setStartLocationOption('saved')}
                />
                <div className="option-content">
                  <span className="option-label">Saved location</span>
                  <span className="option-address">
                    {pendingRouteToStart.starting_address
                      ? `${pendingRouteToStart.starting_address}, ${pendingRouteToStart.starting_city || ''} ${pendingRouteToStart.starting_state || ''}`
                      : 'No saved starting address'}
                  </span>
                </div>
              </label>

              {/* GPS Location Option */}
              <label className={`start-location-option ${startLocationOption === 'gps' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="startLocation"
                  value="gps"
                  checked={startLocationOption === 'gps'}
                  onChange={() => {
                    setStartLocationOption('gps');
                    if (!gpsStartLocation && gpsStartStatus !== 'detecting') {
                      detectGpsForStart();
                    }
                  }}
                />
                <div className="option-content">
                  <span className="option-label">My current GPS location</span>
                  <span className="option-address gps-address">
                    {gpsStartStatus === 'detecting' && '📍 Detecting location...'}
                    {gpsStartStatus === 'success' && gpsStartLocation && `📍 ${gpsStartLocation.address}`}
                    {gpsStartStatus === 'error' && '⚠️ GPS unavailable'}
                    {gpsStartStatus === '' && startLocationOption === 'gps' && '📍 Click to detect...'}
                  </span>
                </div>
              </label>
            </div>

            <div className="start-route-modal-footer">
              <button
                className="btn btn-outline"
                onClick={() => { setShowStartRouteModal(false); setPendingRouteToStart(null); }}
                disabled={startingRoute}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={confirmStartRoute}
                disabled={startingRoute || (startLocationOption === 'gps' && gpsStartStatus === 'detecting')}
              >
                {startingRoute ? 'Starting...' : 'Start Route'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && pendingPaymentStop && (
        <div className="modal-overlay" onClick={() => { setShowPaymentModal(false); setPendingPaymentStop(null); setIsEditingPayment(false); setShowDiscountForm(false); setShowExtraChargeForm(false); setExtraCharge({ amount: '', reason: '' }); setIsComp(false); setCompReason(''); setStopPhotos([]); setSelectedPhoto(null); setSelectedAddons({}); }}>
          <div className="payment-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="payment-modal-header">
              <h2>{isEditingPayment ? 'Update Payment' : 'Mark as Paid?'}</h2>
              <p className="payment-customer-name">
                {pendingPaymentStop.first_name} {pendingPaymentStop.last_name}
              </p>
              <p className="payment-amount">
                {(() => {
                  if (stopAwaitsDepth(pendingPaymentStop)) return AWAITING_DEPTH_LABEL;
                  // Depth route with its depth entered: the band price, not
                  // the customer's flat price.
                  if (pendingPaymentStop.status !== 'completed' && depthPreviewFor(pendingPaymentStop)) {
                    return formatDepthPreview(depthPreviewFor(pendingPaymentStop));
                  }
                  const basePrice = parseFloat(pendingPaymentStop.price_snow) || 35;
                  const saltPrice = saltAppliedToggle ? (parseFloat(pendingPaymentStop.salt_price) || 0) : 0;
                  const extraChargeAmount = parseFloat(extraCharge.amount) || 0;
                  const addonsDollars = addonsAmount(availableAddons, selectedAddons, pendingPaymentStop, basePrice + saltPrice);
                  const totalBeforeDiscount = basePrice + saltPrice + extraChargeAmount + addonsDollars;

                  if (stopDiscount.type && stopDiscount.amount) {
                    const finalPrice = calculateDiscountedPrice(totalBeforeDiscount, parseFloat(stopDiscount.amount), stopDiscount.type);
                    return (
                      <>
                        <span className="original-price-strike">${totalBeforeDiscount.toFixed(2)}</span>
                        <span className="discounted-price-display">
                          ${finalPrice.toFixed(2)}
                        </span>
                      </>
                    );
                  }
                  return `$${totalBeforeDiscount.toFixed(2)}`;
                })()}
                {saltAppliedToggle && <span className="salt-indicator-modal"> +Salt</span>}
                {extraCharge.amount && parseFloat(extraCharge.amount) > 0 && <span className="extra-charge-indicator-modal"> +Extra</span>}
                {Object.values(selectedAddons).some(v => v) && <span className="addons-indicator-modal"> +Add-ons</span>}
              </p>
            </div>

            {/* Photos: first thing under the header, and big enough to hit with
                gloves on. capture="environment" opens the rear camera directly. */}
            <div className="stop-photo-section">
              <button
                type="button"
                className="take-photo-btn"
                onClick={() => photoInputRef.current?.click()}
                disabled={photoPreparing}
              >
                {photoPreparing ? (
                  <><span className="photo-spinner" aria-hidden="true" /> Preparing photo...</>
                ) : (
                  '📷 Take photo'
                )}
              </button>
              <input
                type="file"
                ref={photoInputRef}
                accept="image/*"
                capture="environment"
                onChange={handlePhotoUpload}
                style={{ display: 'none' }}
              />
              {(stopPhotos.length > 0 || pendingPhotos.some(p => p.stopId === pendingPaymentStop.id)) && (
                <div className="photo-thumbnails">
                  {pendingPhotos.filter(p => p.stopId === pendingPaymentStop.id).map(p => (
                    <div key={p.key} className="photo-thumbnail-container">
                      <img
                        src={p.previewUrl}
                        alt="Not yet saved"
                        className={`photo-thumbnail ${p.status === 'failed' ? 'photo-failed' : 'photo-uploading'}`}
                      />
                      {p.status === 'uploading' ? (
                        <span className="photo-thumb-overlay" aria-label="Uploading">
                          <span className="photo-spinner" />
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="photo-thumb-overlay photo-retry-btn"
                          onClick={() => uploadPendingPhoto(p)}
                        >
                          Retry
                        </button>
                      )}
                    </div>
                  ))}
                  {stopPhotos.map(photo => (
                    <div key={photo.id} className="photo-thumbnail-container">
                      <img
                        src={photo.photo_url}
                        alt="Stop"
                        className="photo-thumbnail"
                        onClick={() => setSelectedPhoto(photo)}
                      />
                      <button
                        type="button"
                        className="delete-photo-btn"
                        onClick={() => handleDeletePhoto(photo.id)}
                        aria-label="Delete photo"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Salt Toggle Section - show for "as_needed" customers */}
            {pendingPaymentStop.salt_preference === 'as_needed' && (
              <div className="salt-toggle-section">
                <label className="salt-toggle-label">
                  <input
                    type="checkbox"
                    checked={saltAppliedToggle}
                    onChange={(e) => setSaltAppliedToggle(e.target.checked)}
                  />
                  <span className="salt-toggle-text">
                    Apply Salt (+${(parseFloat(pendingPaymentStop.salt_price) || 0).toFixed(2)})
                  </span>
                </label>
              </div>
            )}

            {/* Service Add-ons Section */}
            {availableAddons.length > 0 && (
              <div className="service-addons-section">
                <label className="service-addons-title">Add-on Services:</label>
                <div className="service-addons-list">
                  {availableAddons.map(addon => {
                    return (
                      <label key={addon.id} className="service-addon-item">
                        <input
                          type="checkbox"
                          checked={selectedAddons[addon.id] || false}
                          onChange={(e) => setSelectedAddons(prev => ({
                            ...prev,
                            [addon.id]: e.target.checked
                          }))}
                        />
                        <span className="addon-name">{addon.name}</span>
                        <span className="addon-price">{addonPriceLabel(addon, pendingPaymentStop)}</span>
                      </label>
                    );
                  })}
                </div>
                {Object.values(selectedAddons).some(v => v) && (
                  <div className="addons-total">
                    Add-ons: +${addonsAmount(
                      availableAddons, selectedAddons, pendingPaymentStop,
                      (parseFloat(pendingPaymentStop.price_snow) || 0) +
                        (saltAppliedToggle ? (parseFloat(pendingPaymentStop.salt_price) || 0) : 0)
                    ).toFixed(2)}
                  </div>
                )}
              </div>
            )}

            {/* Discount and Extra Charge Section */}
            <div className="adjustments-section">
              {/* Discount */}
              <div className="adjustment-item">
                {!showDiscountForm ? (
                  <button
                    className="add-adjustment-btn add-discount-btn"
                    onClick={() => setShowDiscountForm(true)}
                  >
                    + Add Discount
                  </button>
                ) : (
                  <div className="adjustment-form discount-form">
                    <div className="adjustment-form-header">
                      <span className="adjustment-label">Discount</span>
                      <button
                        className="remove-adjustment-btn"
                        onClick={() => {
                          setShowDiscountForm(false);
                          setStopDiscount({ amount: '', type: '', reason: '', ongoing: false });
                        }}
                      >
                        ✕
                      </button>
                    </div>
                    <div className="discount-form-row">
                      <select
                        className="discount-type-select"
                        value={stopDiscount.type}
                        onChange={(e) => setStopDiscount({ ...stopDiscount, type: e.target.value })}
                      >
                        <option value="">Type</option>
                        <option value="dollar">$ Dollar</option>
                        <option value="percent">% Percent</option>
                      </select>
                      <input
                        type="number"
                        className="discount-amount-input"
                        placeholder="Amount"
                        value={stopDiscount.amount}
                        onChange={(e) => setStopDiscount({ ...stopDiscount, amount: e.target.value })}
                        step={stopDiscount.type === 'percent' ? '1' : '0.01'}
                        min="0"
                      />
                    </div>
                    <input
                      type="text"
                      className="adjustment-reason-input"
                      placeholder="Reason (optional)"
                      value={stopDiscount.reason}
                      onChange={(e) => setStopDiscount({ ...stopDiscount, reason: e.target.value })}
                    />
                    {/* Apply to all future visits checkbox - only show for new completions */}
                    {!isEditingPayment && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginTop: '10px' }}>
                        <input
                          type="checkbox"
                          checked={stopDiscount.ongoing}
                          onChange={(e) => setStopDiscount({ ...stopDiscount, ongoing: e.target.checked })}
                          style={{ width: '18px', height: '18px', accentColor: '#2563eb', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Apply to all future visits</span>
                      </label>
                    )}
                    {isEditingPayment && (
                      <button
                        type="button"
                        className="remove-discount-link"
                        onClick={() => {
                          setShowDiscountForm(false);
                          setStopDiscount({ amount: '', type: '', reason: '', ongoing: false });
                        }}
                      >
                        Remove Discount
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Extra Charge */}
              <div className="adjustment-item">
                {!showExtraChargeForm ? (
                  <button
                    className="add-adjustment-btn add-extra-charge-btn"
                    onClick={() => setShowExtraChargeForm(true)}
                  >
                    + Add Extra Charge
                  </button>
                ) : (
                  <div className="adjustment-form extra-charge-form">
                    <div className="adjustment-form-header">
                      <span className="adjustment-label">Extra Charge</span>
                      <button
                        className="remove-adjustment-btn"
                        onClick={() => {
                          setShowExtraChargeForm(false);
                          setExtraCharge({ amount: '', reason: '' });
                        }}
                      >
                        ✕
                      </button>
                    </div>
                    <div className="extra-charge-form-row">
                      <span className="extra-charge-prefix">$</span>
                      <input
                        type="number"
                        className="extra-charge-amount-input"
                        placeholder="0.00"
                        value={extraCharge.amount}
                        onChange={(e) => setExtraCharge({ ...extraCharge, amount: e.target.value })}
                        step="0.01"
                        min="0"
                      />
                    </div>
                    <input
                      type="text"
                      className="adjustment-reason-input"
                      placeholder="Reason (e.g., heavy snow, tip)"
                      value={extraCharge.reason}
                      onChange={(e) => setExtraCharge({ ...extraCharge, reason: e.target.value })}
                    />
                  </div>
                )}
              </div>

              {/* Comp/Free Toggle */}
              <div className="adjustment-item comp-toggle-container">
                <label className="comp-toggle-label">
                  <input
                    type="checkbox"
                    checked={isComp}
                    onChange={(e) => setIsComp(e.target.checked)}
                    className="comp-toggle-checkbox"
                  />
                  <span className="comp-toggle-text">Mark as Comp/Free</span>
                </label>
                {isComp && (
                  <input
                    type="text"
                    className="adjustment-reason-input comp-reason-input"
                    placeholder="Reason for comp (optional)"
                    value={compReason}
                    onChange={(e) => setCompReason(e.target.value)}
                  />
                )}
              </div>

            </div>

            {/* Save Changes button - only when editing completed stop */}
            {isEditingPayment && (
              <div className="save-adjustments-section">
                <button
                  className="save-adjustments-btn"
                  onClick={handleSaveAdjustments}
                  disabled={completing}
                >
                  {completing ? 'Saving...' : 'Save Changes'}
                </button>
                <p className="save-adjustments-hint">
                  Save discount, extra charge, or comp changes without changing payment method
                </p>
              </div>
            )}

            {/* Comp Button - shown when isComp is checked */}
            {isComp && (
              <div className="payment-options comp-submit-section">
                <button
                  className="payment-option-btn comp-submit-btn"
                  onClick={() => handlePaymentSelection('comp', 'paid')}
                >
                  <span className="payment-icon">✓</span>
                  <span>Mark as Comp - Complete Stop</span>
                </button>
              </div>
            )}

            {/* Door payment on a stop awaiting the storm depth (design 3.4) */}
            {!isComp && stopAwaitsDepth(pendingPaymentStop) && (
              <div className="door-amount-section">
                <label className="door-amount-label" htmlFor="door-amount-input">
                  Amount collected
                </label>
                <input
                  id="door-amount-input"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0.01"
                  className="door-amount-input"
                  value={amountCollected}
                  onChange={(e) => { setAmountCollected(e.target.value); setDoorPaymentError(''); }}
                  placeholder="0.00"
                />
                <p className="door-amount-hint">
                  No price yet: it is set when the storm depth is entered. Required for every method except Square.
                </p>
                {doorPaymentError && <p className="door-amount-error" role="alert">{doorPaymentError}</p>}
              </div>
            )}

            {/* Payment methods: a Paid/Not Paid pair for every method that is on,
                built-in or custom, then Square's invoice section (hidden when comp
                is checked) */}
            {!isComp && (
              <CompletionPaymentChoices
                methods={paymentMethods}
                awaitingDepth={stopAwaitsDepth(pendingPaymentStop)}
                onSelect={handlePaymentSelection}
              />
            )}

            {/* Mark as Skipped option - only for admins when editing a completed stop */}
            {isEditingPayment && isAdmin() && (
              <>
                <div className="payment-section-divider">
                  <span>Change Status</span>
                </div>
                <div className="payment-options status-change-options">
                  <button
                    className="payment-option-btn mark-skipped-btn"
                    onClick={markCompletedAsSkipped}
                  >
                    <span className="payment-icon">—</span>
                    <span>Mark as Skipped</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Add Customer Modal */}
      {showAddCustomer && (
        <div className="modal-overlay" onClick={() => { setShowAddCustomer(false); setShowNewCustomerForm(false); }}>
          <div className="add-customer-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="modal-header">
              <h2>{showNewCustomerForm ? 'New Storm Call' : 'Add Customer to Route'}</h2>
              <button className="modal-close" onClick={() => { setShowAddCustomer(false); setShowNewCustomerForm(false); }} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              {!showNewCustomerForm ? (
                <>
                  {/* Add New Customer Button - Hidden for drivers and crew leads */}
                  {isAdmin() && (
                    <>
                      <button
                        className="new-customer-btn"
                        onClick={() => setShowNewCustomerForm(true)}
                      >
                        + Add New Customer (Storm Call)
                      </button>

                      <div className="modal-divider">
                        <span>or select existing</span>
                      </div>
                    </>
                  )}

                  <input
                    type="text"
                    className="customer-search-input"
                    placeholder="Search customers..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    autoFocus
                  />
                  <div className="customer-list">
                    {filteredCustomers.length === 0 ? (
                      <div className="no-customers">
                        {availableCustomers.length === 0
                          ? 'All customers are already on this route'
                          : 'No customers match your search'}
                      </div>
                    ) : (
                      filteredCustomers.map((customer) => (
                        <button
                          key={customer.id}
                          className={`customer-select-item${customer.request.noRequest ? ' no-request' : ''}`}
                          onClick={() => addCustomerMidRoute(customer)}
                          disabled={addingCustomer}
                        >
                          <div className="customer-info">
                            <div className="customer-name">
                              {customer.first_name} {customer.last_name}
                              <span className={`service-frequency-badge ${preferenceBadge(customer.servicePreference).className}`}>
                                {preferenceBadge(customer.servicePreference).label}
                              </span>
                              {customer.request.requestedDate && (
                                <span className="request-badge">{requestBadgeText(customer.request.requestedDate)}</span>
                              )}
                              {customer.request.noRequest && (
                                <span className="no-request-badge">{NO_REQUEST_LABEL}</span>
                              )}
                              {customer.do_not_serve && (
                                <DoNotServeBadge reason={customer.do_not_serve_reason} />
                              )}
                            </div>
                            <div className="customer-address">
                              {customer.address}, {customer.city}
                            </div>
                          </div>
                          <div className="add-indicator">
                            {addingCustomer ? '...' : '+'}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </>
              ) : (
                /* New Customer Form */
                <div className="new-customer-form">
                  <button
                    className="back-btn"
                    onClick={() => setShowNewCustomerForm(false)}
                  >
                    Back to Search
                  </button>

                  <div className="form-row">
                    <input
                      type="text"
                      placeholder="First Name *"
                      value={newCustomer.firstName}
                      onChange={(e) => setNewCustomer({ ...newCustomer, firstName: e.target.value })}
                      autoFocus
                    />
                    <input
                      type="text"
                      placeholder="Last Name"
                      value={newCustomer.lastName}
                      onChange={(e) => setNewCustomer({ ...newCustomer, lastName: e.target.value })}
                    />
                  </div>

                  <input
                    type="text"
                    placeholder="Street Address *"
                    value={newCustomer.address}
                    onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                  />

                  <div className="form-row form-row-3">
                    <input
                      type="text"
                      placeholder="City *"
                      value={newCustomer.city}
                      onChange={(e) => setNewCustomer({ ...newCustomer, city: e.target.value })}
                    />
                    <input
                      type="text"
                      placeholder="State *"
                      value={newCustomer.state}
                      onChange={(e) => setNewCustomer({ ...newCustomer, state: e.target.value.toUpperCase() })}
                      maxLength={2}
                      style={{ maxWidth: '70px' }}
                    />
                    <input
                      type="text"
                      placeholder="Zip *"
                      value={newCustomer.zip}
                      onChange={(e) => setNewCustomer({ ...newCustomer, zip: e.target.value })}
                      maxLength={10}
                      style={{ maxWidth: '100px' }}
                    />
                  </div>

                  <div className="form-row">
                    <input
                      type="tel"
                      placeholder="Phone"
                      value={newCustomer.phone}
                      onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                    />
                    <input
                      type="email"
                      placeholder="Email"
                      value={newCustomer.email}
                      onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                    />
                  </div>

                  <div className="form-section-title">Service Details</div>

                  <div className="form-row">
                    <div className="form-field">
                      <label className="form-field-label">Tier</label>
                      <select
                        value={newCustomer.snowTierId}
                        onChange={(e) => handleTierChange(e.target.value)}
                      >
                        {pricingTiers.map(tier => (
                          <option key={tier.id} value={String(tier.id)}>
                            {tier.name} - ${parseFloat(tier.default_price).toFixed(2)}
                          </option>
                        ))}
                        <option value="custom">Custom</option>
                      </select>
                    </div>
                    <div className="form-field">
                      <label className="form-field-label">Payment Method</label>
                      <select
                        value={newCustomer.paymentMethodPreferred}
                        onChange={(e) => setNewCustomer({ ...newCustomer, paymentMethodPreferred: e.target.value })}
                      >
                        {preferredMethodOptions(paymentMethods, newCustomer.paymentMethodPreferred).map(method => (
                          <option key={method.id} value={method.id}>{method.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-row form-row-3">
                    <div className="form-field">
                      <label className="form-field-label">Base Price *</label>
                      <div className="price-input-wrapper">
                        <span className="price-prefix">$</span>
                        <input
                          type="number"
                          value={newCustomer.priceSnow}
                          onChange={(e) => setNewCustomer({ ...newCustomer, priceSnow: e.target.value, snowTierId: 'custom' })}
                          step="0.01"
                          min="0"
                        />
                      </div>
                    </div>
                    <div className="form-field">
                      <label className="form-field-label">Salt Price</label>
                      <div className="price-input-wrapper">
                        <span className="price-prefix">$</span>
                        <input
                          type="number"
                          value={newCustomer.saltPrice}
                          onChange={(e) => setNewCustomer({ ...newCustomer, saltPrice: e.target.value, snowTierId: 'custom' })}
                          step="0.01"
                          min="0"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">Service Preference</label>
                    <select
                      value={newCustomer.serviceFrequency}
                      onChange={(e) => setNewCustomer({ ...newCustomer, serviceFrequency: e.target.value })}
                    >
                      <option value="seasonal">Seasonal (contracted for the season)</option>
                      <option value="as_needed">On-Call / As Needed</option>
                    </select>
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">How did you find me?</label>
                    <select
                      value={newCustomer.leadSource}
                      onChange={(e) => setNewCustomer({ ...newCustomer, leadSource: e.target.value })}
                    >
                      <option value="">-- Select --</option>
                      {enabledLeadSources.map(source => (
                        <option key={source.id} value={source.id}>{source.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">Services Included</label>
                    <div className="checkbox-grid">
                      <label className="checkbox-item">
                        <input
                          type="checkbox"
                          checked={newCustomer.driveway}
                          onChange={(e) => setNewCustomer({ ...newCustomer, driveway: e.target.checked })}
                        />
                        <span>Driveway</span>
                      </label>
                      <label className="checkbox-item">
                        <input
                          type="checkbox"
                          checked={newCustomer.sidewalks}
                          onChange={(e) => setNewCustomer({ ...newCustomer, sidewalks: e.target.checked })}
                        />
                        <span>Sidewalks</span>
                      </label>
                      <label className="checkbox-item">
                        <input
                          type="checkbox"
                          checked={newCustomer.walkways}
                          onChange={(e) => setNewCustomer({ ...newCustomer, walkways: e.target.checked })}
                        />
                        <span>Walkways</span>
                      </label>
                      <label className="checkbox-item">
                        <input
                          type="checkbox"
                          checked={newCustomer.salt}
                          onChange={(e) => setNewCustomer({ ...newCustomer, salt: e.target.checked })}
                        />
                        <span>Salt</span>
                      </label>
                    </div>
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">Notes</label>
                    <textarea
                      placeholder="Special instructions, gate codes, etc."
                      value={newCustomer.notes}
                      onChange={(e) => setNewCustomer({ ...newCustomer, notes: e.target.value })}
                      rows={3}
                    />
                  </div>

                  <button
                    className="btn btn-primary create-customer-btn"
                    onClick={createAndAddCustomer}
                    disabled={addingCustomer}
                  >
                    {addingCustomer ? 'Creating...' : 'Create & Add to Route'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Phone Action Popup */}
      {phonePopup.show && (
        <div
          ref={phonePopupRef}
          className="phone-action-popup"
          style={{
            position: 'fixed',
            left: phonePopup.x,
            top: phonePopup.y,
            zIndex: 9999
          }}
        >
          <button
            className="phone-action-option"
            onClick={() => {
              window.location.href = `tel:${phonePopup.phone}`;
              setPhonePopup({ show: false, phone: '', x: 0, y: 0 });
            }}
          >
            <span className="phone-action-icon">📞</span>
            <span>Call</span>
          </button>
          <div className="phone-action-divider"></div>
          <button
            className="phone-action-option"
            onClick={() => {
              window.location.href = `sms:${phonePopup.phone}`;
              setPhonePopup({ show: false, phone: '', x: 0, y: 0 });
            }}
          >
            <span className="phone-action-icon">💬</span>
            <span>Text</span>
          </button>
        </div>
      )}

      {/* Billing Modal */}
      {showBillingModal && (
        <div className="modal-overlay" onClick={() => !billingInProgress && setShowBillingModal(false)}>
          <div className="billing-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="billing-modal-header">
              <h2>Bill Customers</h2>
              <button
                className="modal-close-btn"
                onClick={() => !billingInProgress && setShowBillingModal(false)}
                disabled={billingInProgress}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="billing-modal-content">
              {billingResults ? (
                /* Show results after billing */
                <div className="billing-results" aria-live="polite">
                  <div className="billing-results-header">
                    <h3>{billingResults.error ? 'Billing Unavailable' : 'Billing Complete'}</h3>
                    {billingResults.sent?.length > 0 && (
                      <span className="results-summary success">
                        ✓ {billingResults.sent.length} sent
                      </span>
                    )}
                    {billingResults.failed?.length > 0 && (
                      <span className="results-summary failed">
                        ✗ {billingResults.failed.length} failed
                      </span>
                    )}
                  </div>
                  {billingResults.error && (
                    <p className="billing-error-message">{billingResults.error}</p>
                  )}
                  <div className="billing-results-list">
                    {billingResults.sent?.map((item, i) => (
                      <div key={`sent-${i}`} className="billing-result-item success">
                        <span className="result-icon">✓</span>
                        <span className="result-name">{item.name}</span>
                        <span className="result-amount">{formatCurrency(item.amount)}</span>
                        <span className="result-status">Invoice sent</span>
                      </div>
                    ))}
                    {billingResults.failed?.map((item, i) => (
                      <div key={`failed-${i}`} className="billing-result-item failed">
                        <span className="result-icon">✗</span>
                        <span className="result-name">{item.name}</span>
                        <span className="result-reason">{item.reason}</span>
                      </div>
                    ))}
                  </div>
                  {!billingResults.error && billingResults.sent?.length === 0 && billingResults.failed?.length === 0 && (
                    <p className="no-results">No invoices were processed</p>
                  )}
                </div>
              ) : (
                /* Show billing preview */
                (() => {
                  const categories = getBillingCategories();
                  const hasAutoBill = categories.autoBill.length > 0;
                  const hasNoEmail = categories.noEmail.length > 0;
                  const hasManual = categories.manual.length > 0;

                  return (
                    <>
                      {hasAutoBill && (
                        <div className="billing-section">
                          <h3 className="billing-section-title">
                            <span className="billing-icon">💳</span>
                            Square (auto-invoice)
                            <button
                              className="btn-link select-all-btn"
                              onClick={() => {
                                const allIds = categories.autoBill.map(s => s.id);
                                if (selectedBillStops.size === allIds.length) {
                                  setSelectedBillStops(new Set());
                                } else {
                                  setSelectedBillStops(new Set(allIds));
                                }
                              }}
                              style={{ marginLeft: 'auto', fontSize: '0.8rem' }}
                            >
                              {selectedBillStops.size === categories.autoBill.length ? 'Deselect All' : 'Select All Unpaid'}
                            </button>
                          </h3>
                          {categories.autoBill.map(stop => (
                            <div
                              key={stop.id}
                              className="billing-item auto-bill"
                              onClick={() => {
                                setSelectedBillStops(prev => {
                                  const next = new Set(prev);
                                  if (next.has(stop.id)) next.delete(stop.id);
                                  else next.add(stop.id);
                                  return next;
                                });
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              <input
                                type="checkbox"
                                checked={selectedBillStops.has(stop.id)}
                                onChange={() => {}}
                                style={{ marginRight: 8, cursor: 'pointer' }}
                              />
                              <span className="billing-name">{stop.customerName}</span>
                              <span className="billing-amount">{formatCurrency(stop.amount)}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {hasNoEmail && (
                        <div className="billing-section">
                          <h3 className="billing-section-title warning">
                            <span className="billing-icon">⚠️</span>
                            Cannot auto-bill (no email)
                          </h3>
                          {categories.noEmail.map(stop => (
                            <div key={stop.id} className="billing-item no-email">
                              <span className="billing-warning">⚠</span>
                              <span className="billing-name">{stop.customerName}</span>
                              <span className="billing-amount">{formatCurrency(stop.amount)}</span>
                              <span className="billing-method">Square</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {hasManual && (
                        <div className="billing-section">
                          <h3 className="billing-section-title manual">
                            <span className="billing-icon">📋</span>
                            Manual billing required
                          </h3>
                          {categories.manual.map(stop => (
                            <div key={stop.id} className="billing-item manual">
                              <span className="billing-manual">📋</span>
                              <span className="billing-name">{stop.customerName}</span>
                              <span className="billing-amount">{formatCurrency(stop.amount)}</span>
                              <span className="billing-method">{getPaymentMethodName(stop.payment_method)}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {!hasAutoBill && !hasNoEmail && !hasManual && (
                        <p className="no-billable">No unpaid customers to bill</p>
                      )}
                    </>
                  );
                })()
              )}
            </div>

            <div className="billing-modal-footer">
              {billingResults ? (
                <button
                  className="btn btn-primary done-btn"
                  onClick={() => {
                    setShowBillingModal(false);
                    setBillingResults(null);
                  }}
                >
                  Done
                </button>
              ) : (
                <>
                  <button
                    className="btn btn-outline"
                    onClick={() => setShowBillingModal(false)}
                    disabled={billingInProgress}
                  >
                    Cancel
                  </button>
                  {selectedBillStops.size > 0 && (
                    <button
                      className="btn btn-primary"
                      onClick={() => handleBillSelected(Array.from(selectedBillStops))}
                      disabled={billingInProgress}
                    >
                      {billingInProgress ? 'Sending...' : `Bill Selected (${selectedBillStops.size})`}
                    </button>
                  )}
                  {getBillingCategories().autoBill.length > 0 && (
                    <button
                      className="btn btn-primary"
                      onClick={handleBillAll}
                      disabled={billingInProgress}
                    >
                      {billingInProgress ? 'Sending...' : `Bill All (${getBillingCategories().autoBill.length})`}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Photo Preview Modal */}
      {selectedPhoto && (
        <div className="modal-overlay photo-preview-overlay" onClick={() => setSelectedPhoto(null)}>
          <div className="photo-preview-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <button className="photo-preview-close" onClick={() => setSelectedPhoto(null)} aria-label="Close">✕</button>
            <img src={selectedPhoto.photo_url} alt="Full size" className="photo-preview-image" />
            {selectedPhoto.caption && <p className="photo-preview-caption">{selectedPhoto.caption}</p>}
          </div>
        </div>
      )}

      <style>{`
        .storm-header {
          background: linear-gradient(135deg, var(--color-secondary) 0%, var(--color-secondary-dark) 100%);
        }

        .header-nav {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 8px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: all 0.15s ease;
        }
        .nav-link:hover {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .nav-link.active {
          background: rgba(255,255,255,0.2);
          color: white;
        }
        .storm-active {
          background: rgba(0,0,0,0.2) !important;
        }
        .admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%) !important;
          color: white !important;
        }
        .admin-link:hover {
          background: linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%) !important;
        }
        .logout-link {
          margin-left: 8px;
          border-left: 1px solid rgba(255,255,255,0.2);
          padding-left: 16px;
        }

        .storm-container {
          max-width: 800px;
        }

        .storm-title {
          font-size: 2rem;
          font-weight: 700;
          margin: 0 0 8px 0;
          color: var(--color-secondary);
        }
        .storm-subtitle {
          color: var(--color-text-light);
          margin: 0 0 16px 0;
        }

        /* Route Controls (Filter + Sort) */
        .route-controls {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 20px;
        }
        .sort-toggle {
          padding: 8px 14px;
          border: 2px solid var(--color-border);
          border-radius: 20px;
          background: var(--color-card);
          cursor: pointer;
          font-size: 0.85rem;
          font-weight: 500;
          color: var(--color-text-light);
          transition: all 0.2s ease;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .sort-toggle:hover {
          border-color: var(--color-primary);
          color: var(--color-primary);
        }

        /* Route Filter Tabs */
        .route-filter-tabs {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .filter-tab {
          padding: 10px 20px;
          border: 2px solid var(--color-border);
          border-radius: 25px;
          background: var(--color-card);
          cursor: pointer;
          font-size: 0.9rem;
          font-weight: 500;
          transition: all 0.2s ease;
        }
        .filter-tab:hover {
          border-color: var(--color-secondary);
        }
        .filter-tab.active {
          background: var(--color-secondary);
          border-color: var(--color-secondary);
          color: white;
        }
        .empty-filter-state {
          text-align: center;
          padding: 32px 16px;
          color: var(--color-text-light);
        }

        /* Route Status Badges in Header */
        .route-status-badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 600;
          margin-left: 8px;
        }
        .route-status-badge.completed {
          background: #d1fae5;
          color: var(--color-success, #059669);
        }
        .route-status-badge.archived {
          background: #e5e7eb;
          color: #6b7280;
        }

        .empty-state {
          text-align: center;
          padding: 48px 16px;
          color: var(--color-text-light);
        }
        .empty-state .btn {
          margin-top: 16px;
        }

        /* Route Selection */
        .route-select-grid {
          display: grid;
          gap: 16px;
        }
        .route-select-card {
          background: var(--color-card);
          border: 2px solid var(--color-border);
          border-radius: 16px;
          padding: 20px;
          text-align: left;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .route-select-card:hover {
          border-color: var(--color-secondary);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }
        .route-select-card.completed {
          opacity: 0.85;
          border-color: #10b981;
        }
        .route-select-card.archived {
          opacity: 0.6;
          border-style: dashed;
        }
        .route-select-name {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 8px;
        }
        .route-select-meta {
          display: flex;
          gap: 12px;
          align-items: center;
          color: var(--color-text-light);
          font-size: 0.875rem;
          flex-wrap: wrap;
        }

        .route-total {
          font-size: 1rem;
          font-weight: 700;
          color: var(--color-success);
          background: rgba(76, 175, 80, 0.1);
          padding: 4px 10px;
          border-radius: 6px;
        }
        .driver-pay-badge {
          font-size: 0.9rem;
          font-weight: 600;
          color: #2196F3;
          background: rgba(33, 150, 243, 0.1);
          padding: 4px 10px;
          border-radius: 6px;
        }
        .assigned-badge {
          font-size: 0.75rem;
          background: #8b5cf6;
          color: white;
          padding: 2px 8px;
          border-radius: 4px;
          font-weight: 500;
        }
        .assigned-badge.unassigned {
          background: #6b7280;
        }

        /* Payment breakdown styles */
        .payment-breakdown {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 0.8rem;
          padding: 3px 8px;
          border-radius: 4px;
          background: rgba(0, 0, 0, 0.03);
        }
        .payment-fully-paid {
          color: #10b981;
          font-weight: 600;
        }
        .payment-paid {
          color: #10b981;
        }
        .payment-unpaid {
          color: var(--color-error);
        }
        .payment-separator {
          color: #d1d5db;
          margin: 0 2px;
        }

        .status-badge {
          font-size: 0.75rem;
          padding: 4px 10px;
          border-radius: 12px;
          font-weight: 500;
        }
        .status-badge.in-progress {
          background: #fef3c7;
          color: #92400e;
        }
        .status-badge.completed {
          background: #d1fae5;
          color: #065f46;
        }
        .status-badge.not-started {
          background: var(--color-bg-light, #f3f4f6);
          color: #6b7280;
        }
        .status-badge.archived {
          background: #e5e7eb;
          color: #6b7280;
        }

        /* Active Route */
        .route-header {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 16px;
        }
        .route-header-top {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .btn-back {
          flex-shrink: 0;
        }
        .route-title {
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0;
        }
        .route-header-meta {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        .progress-info {
          color: var(--color-text-light);
          font-size: 0.875rem;
        }
        .route-header-actions {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .progress-bar-container {
          height: 8px;
          background: var(--color-border);
          border-radius: 4px;
          margin-bottom: 24px;
          overflow: hidden;
        }
        .progress-bar-fill {
          height: 100%;
          background: var(--color-secondary);
          border-radius: 4px;
          transition: width 0.3s ease;
        }

        .current-location-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: var(--color-background);
          border: 1px solid var(--color-border);
          border-radius: 10px;
          padding: 12px 16px;
          margin-bottom: 20px;
          gap: 12px;
          flex-wrap: wrap;
        }
        .current-location-info {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
          flex: 1;
          min-width: 200px;
        }
        .current-location-label {
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          color: var(--color-text-light);
        }
        .current-location-name {
          font-weight: 600;
          color: var(--color-secondary);
        }
        .current-location-address {
          font-size: 0.875rem;
          color: var(--color-text-light);
        }
        .reoptimize-container {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }
        .gps-status {
          font-size: 0.8rem;
          padding: 4px 10px;
          border-radius: 6px;
          white-space: nowrap;
        }
        .gps-status-acquiring {
          background: #dbeafe;
          color: #1d4ed8;
        }
        .gps-status-success {
          background: #dcfce7;
          color: #166534;
        }
        .gps-status-unavailable {
          background: #fef3c7;
          color: #92400e;
        }

        .stops-section {
          margin-bottom: 32px;
        }
        .stops-section-title {
          font-size: 1rem;
          font-weight: 600;
          margin: 0 0 16px 0;
          color: var(--color-text-light);
        }

        .storm-stops-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .storm-stop-card {
          background: var(--color-card);
          border-radius: 16px;
          padding: 20px;
          display: flex;
          gap: 16px;
          align-items: flex-start;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }
        .storm-stop-card.next-up {
          border: 2px solid var(--color-secondary);
          background: linear-gradient(135deg, rgba(255,152,0,0.05) 0%, rgba(255,152,0,0.02) 100%);
        }

        .stop-order-badge {
          width: 40px;
          height: 40px;
          background: var(--color-secondary);
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1.125rem;
          flex-shrink: 0;
        }

        .stop-details {
          flex: 1;
        }
        .stop-customer-name {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 4px;
        }
        .stop-address {
          color: var(--color-text);
        }
        .stop-city {
          color: var(--color-text-light);
          font-size: 0.875rem;
          margin-bottom: 8px;
        }

        .stop-addon-lines {
          color: #065f46;
          font-size: 0.875rem;
          font-weight: 600;
          margin: -4px 0 8px;
        }
        .stop-phone {
          color: var(--color-primary);
          font-size: 0.875rem;
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
          text-decoration: underline;
          font-family: inherit;
        }
        .stop-phone:hover {
          color: var(--color-primary-dark);
        }
        .phone-action-popup {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          overflow: hidden;
          min-width: 140px;
        }
        .phone-action-option {
          display: flex;
          align-items: center;
          gap: 12px;
          width: 100%;
          padding: 14px 18px;
          background: none;
          border: none;
          cursor: pointer;
          font-size: 1rem;
          font-family: inherit;
          color: var(--color-text);
          transition: background 0.15s ease;
        }
        .phone-action-option:hover {
          background: var(--color-bg-light, #f3f4f6);
        }
        .phone-action-option:active {
          background: #e5e7eb;
        }
        .phone-action-icon {
          font-size: 1.25rem;
        }
        .phone-action-divider {
          height: 1px;
          background: var(--color-border);
        }
        .stop-price {
          margin-top: 8px;
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        .stop-price .regular-price {
          font-weight: 600;
          color: var(--color-text);
          font-size: 1rem;
        }
        .stop-price .original-price {
          text-decoration: line-through;
          color: #9ca3af;
          font-size: 0.875rem;
        }
        .stop-price .discounted-price {
          font-weight: 700;
          color: #10b981;
          font-size: 1.1rem;
        }
        .referral-discount-badge {
          background: #10b981;
          color: white;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 500;
        }
        .salt-badge {
          background: #d1fae5;
          color: var(--color-success, #059669);
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 0.7rem;
          font-weight: 600;
        }
        .extra-charge-badge {
          background: #fef3c7;
          color: #d97706;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 0.7rem;
          font-weight: 600;
        }
        .extra-charge-indicator {
          background: #fef3c7;
          color: #d97706;
          padding: 1px 5px;
          border-radius: 3px;
          font-size: 0.7rem;
          font-weight: 600;
          margin-left: 6px;
        }
        .service-frequency-badge {
          text-transform: uppercase;
          padding: 2px 8px;
          border-radius: 10px;
          font-size: 0.65rem;
          font-weight: 600;
          margin-left: 8px;
          white-space: nowrap;
        }
        .customer-select-item.no-request {
          opacity: 0.55;
        }
        .request-badge,
        .no-request-badge {
          display: inline-block;
          margin-left: 6px;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
        }
        .request-badge {
          background: #dcfce7;
          color: #166534;
        }
        .no-request-badge {
          background: #e5e7eb;
          color: #374151;
        }
        .service-frequency-badge.seasonal {
          background: rgba(59, 130, 246, 0.15);
          color: #1d4ed8;
        }
        .service-frequency-badge.as-needed {
          background: rgba(249, 115, 22, 0.15);
          color: #c2410c;
        }
        .salt-pref-badge {
          padding: 2px 8px;
          border-radius: 10px;
          font-size: 0.65rem;
          font-weight: 600;
          margin-left: 8px;
          white-space: nowrap;
        }
        .salt-pref-badge.always {
          background: #dbeafe;
          color: #1d4ed8;
        }
        .salt-pref-badge.never {
          background: #fee2e2;
          color: var(--color-error);
        }
        .salt-pref-badge.as_needed {
          background: #fef3c7;
          color: #d97706;
        }
        .payment-method-badge {
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 0.7rem;
          font-weight: 600;
        }
        .payment-method-badge.cash {
          background: #dcfce7;
          color: #16a34a;
        }
        .payment-method-badge.venmo {
          background: #dbeafe;
          color: #2563eb;
        }
        .payment-method-badge.square {
          background: #e0e7ff;
          color: #4338ca;
        }
        .payment-method-badge.cashapp {
          background: #d1fae5;
          color: var(--color-success, #059669);
        }
        .stop-notes {
          margin-top: 8px;
          font-size: 0.85rem;
          color: #6b7280;
          font-style: italic;
          padding: 6px 10px;
          background: #f9fafb;
          border-left: 3px solid #d1d5db;
          border-radius: 0 4px 4px 0;
        }
        .arrival-indicator {
          display: inline-block;
          margin-top: 6px;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 600;
        }
        .completed-info .arrival-indicator {
          display: block;
          width: fit-content;
          margin-top: 2px;
        }
        .arrival-indicator.arrival-pending {
          background: #e0e7ff;
          color: #3730a3;
        }
        .arrival-indicator.arrival-sent {
          background: #d1fae5;
          color: #065f46;
        }
        .arrival-indicator.arrival-none {
          background: #f3f4f6;
          color: #4b5563;
        }
        .arrival-indicator.arrival-failed {
          background: #fee2e2;
          color: #991b1b;
        }
        .stop-discount-reason {
          font-size: 0.8rem;
          color: var(--color-success, #059669);
          font-style: italic;
          margin-top: 4px;
        }
        .discount-badge {
          background: rgba(5, 150, 105, 0.15);
          color: var(--color-success, #059669);
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 0.7rem;
          font-weight: 600;
        }

        /* Completed stops display */
        .completed-info {
          display: flex;
          flex-direction: column;
          flex: 1;
          min-width: 0;
        }
        .completed-discount-info {
          font-size: 0.7rem;
          color: var(--color-success, #059669);
          font-style: italic;
          margin-top: 2px;
        }
        .completed-stop-item.has-discount {
          background: rgba(5, 150, 105, 0.03);
        }
        /* "No price set" standing where a figure would. Muted and italic so a
           driver scanning a column of dollar amounts does not read it as one.
           Applied to stop cards, completed rows and the summary table alike. */
        .depth-band-chip {
          display: inline-block;
          margin-left: 6px;
          padding: 1px 6px;
          border-radius: 10px;
          font-size: 0.72rem;
          font-weight: 600;
          background: var(--color-bg-secondary, #eef2f7);
          color: var(--color-text-light, #475569);
          white-space: nowrap;
        }

        .depth-preview-price {
          white-space: normal;
        }

        .price-unset {
          color: var(--color-text-light, #9ca3af);
          font-style: italic;
          font-weight: 400;
          white-space: nowrap;
        }
        .completed-price {
          font-size: 0.85rem;
          margin-left: auto;
          margin-right: 12px;
          color: var(--color-text);
          text-align: right;
          flex-shrink: 0;
        }
        .completed-price .price-strike {
          text-decoration: line-through;
          color: #9ca3af;
          margin-right: 4px;
        }
        .completed-price .price-discounted {
          color: var(--color-success, #059669);
          font-weight: 600;
        }
        .completed-price .comp-badge {
          background: #dbeafe;
          color: #1d4ed8;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .stop-actions {
          display: flex;
          flex-direction: column;
          gap: 8px;
          align-items: center;
        }
        .stop-complete-btn {
          min-width: 80px;
          font-size: 1.125rem;
          padding: 16px 24px;
        }
        .stop-secondary-actions {
          display: flex;
          gap: 8px;
        }

        /* Completed Stops */
        .completed-section {
          opacity: 0.7;
        }
        .completed-stops-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .completed-stop-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: var(--color-background);
          border-radius: 8px;
          transition: all 0.15s ease;
        }
        .completed-stop-item.editable {
          cursor: pointer;
        }
        .completed-stop-item.editable:hover {
          background: var(--color-card);
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        .completed-stop-item.editable:active {
          transform: scale(0.98);
        }
        .completed-icon {
          width: 24px;
          height: 24px;
          background: #10b981;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.875rem;
          flex-shrink: 0;
        }
        .completed-icon.skipped {
          background: #9ca3af;
        }
        .completed-name {
          font-weight: 500;
          flex-shrink: 0;
        }
        .completed-address {
          color: var(--color-text-light);
          font-size: 0.875rem;
          flex: 1;
          text-align: right;
        }

        /* Completed Route View */
        .completed-route-view {
          padding: 0;
        }
        .route-complete-banner {
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
          padding: 16px 20px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }
        .complete-check {
          background: rgba(255,255,255,0.2);
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
        }
        .route-total-banner {
          margin-left: auto;
          font-weight: 700;
          font-size: 1.1rem;
        }
        .payment-summary-bar {
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--color-card);
          border: 2px solid var(--color-border);
          border-radius: 12px;
          padding: 12px 16px;
          margin-bottom: 16px;
          gap: 0;
        }
        .payment-summary-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 0 20px;
          flex: 1;
        }
        .payment-summary-label {
          font-size: 0.75rem;
          font-weight: 500;
          color: var(--color-text-light);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 4px;
        }
        .payment-summary-amount {
          font-size: 1.1rem;
          font-weight: 700;
        }
        .payment-summary-item.paid .payment-summary-amount {
          color: #10b981;
        }
        .payment-summary-item.unpaid .payment-summary-amount {
          color: var(--color-error);
        }
        .payment-summary-item.total .payment-summary-amount {
          color: var(--color-text);
        }
        .payment-summary-divider {
          width: 1px;
          height: 36px;
          background: var(--color-border);
        }
        .complete-actions-bar {
          display: flex;
          gap: 12px;
          margin-bottom: 20px;
        }
        /* Depth pricing: banner, door amount, awaiting label, bill the difference */
        .depth-pending-banner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
          background: #fef3c7;
          border: 1px solid #fcd34d;
          color: #92400e;
          border-radius: 8px;
          padding: 10px 14px;
          margin: 12px 0;
          font-weight: 500;
        }
        .depth-pending-btn {
          background: #d97706;
          color: #fff;
          border: none;
        }
        .depth-pending-btn:hover { background: #b45309; }
        .door-amount-section {
          background: #fffbeb;
          border: 1px solid #fde68a;
          border-radius: 8px;
          padding: 12px;
          margin: 12px 0;
        }
        .door-amount-label { display: block; font-weight: 600; margin-bottom: 6px; }
        .door-amount-input {
          width: 100%;
          font-size: 1.25rem;
          padding: 10px 12px;
          border: 1px solid var(--color-border, #d1d5db);
          border-radius: 8px;
          box-sizing: border-box;
        }
        .door-amount-hint { font-size: 0.8rem; color: #92400e; margin: 6px 0 0; }
        .door-amount-error { font-size: 0.85rem; color: #b91c1c; font-weight: 600; margin: 6px 0 0; }
        .square-depth-hint { text-align: center; width: 100%; }
        .payment-option-btn:disabled { opacity: 0.45; cursor: not-allowed; }
        .awaiting-depth-label {
          color: #92400e;
          background: #fef3c7;
          border-radius: 10px;
          padding: 1px 8px;
          font-size: 0.8rem;
          font-weight: 600;
          white-space: nowrap;
        }
        .bill-diff-btn {
          background: #fff;
          border: 1px solid #d97706;
          color: #92400e;
          font-size: 0.75rem;
          padding: 4px 8px;
          white-space: nowrap;
        }
        .bill-diff-result { display: block; font-size: 0.75rem; margin-top: 4px; }
        .bill-diff-result.ok { color: #166534; }
        .bill-diff-result.err { color: #b91c1c; }

        .completed-stops-detail-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .completed-stop-card {
          background: var(--color-card);
          border: 2px solid var(--color-border);
          border-radius: 12px;
          padding: 16px;
          display: flex;
          gap: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .completed-stop-card:hover {
          border-color: var(--color-primary);
        }
        .completed-stop-card.skipped {
          opacity: 0.6;
          cursor: default;
          border-style: dashed;
        }
        .completed-stop-card.skipped:hover {
          border-color: var(--color-border);
        }
        .completed-stop-card.skipped.admin-clickable {
          cursor: pointer;
          opacity: 0.7;
        }
        .completed-stop-card.skipped.admin-clickable:hover {
          border-color: #f97316;
          opacity: 0.85;
        }
        .completed-stop-item.admin-clickable {
          cursor: pointer;
        }
        .completed-stop-item.admin-clickable:hover {
          background: rgba(249, 115, 22, 0.1);
        }
        .skipped-label {
          background: #e5e7eb;
          color: #6b7280;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 0.75rem;
          margin-left: 8px;
        }
        .stop-payment-status {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 4px;
          min-width: 100px;
        }
        .tap-to-edit {
          font-size: 0.7rem;
          color: var(--color-text-light);
        }

        /* Legacy Route Complete (keeping for compatibility) */
        .route-complete-message {
          text-align: center;
          padding: 48px 16px;
        }
        .complete-icon {
          width: 80px;
          height: 80px;
          background: #10b981;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2.5rem;
          margin: 0 auto 24px;
        }
        .route-complete-message h2 {
          font-size: 1.75rem;
          margin: 0 0 8px 0;
          color: #10b981;
        }
        .route-complete-message p {
          color: var(--color-text-light);
          margin: 0 0 24px 0;
        }

        /* Mobile Menu */
        .mobile-nav {
          display: flex;
          flex-direction: column;
          padding: 8px 16px 16px;
          border-top: 1px solid rgba(255,255,255,0.1);
        }
        .mobile-nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 12px 8px;
          text-align: left;
          font-size: 1rem;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.15s ease;
        }
        .mobile-nav-link:hover, .mobile-nav-link.active {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .mobile-nav-link.storm-active {
          background: rgba(0,0,0,0.2);
        }
        .mobile-nav-link.admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.logout-link {
          margin-top: 8px;
          border-top: 1px solid rgba(255,255,255,0.1);
          padding-top: 16px;
        }

        @media (max-width: 900px) {
          .desktop-nav {
            display: none;
          }
        }

        @media (max-width: 768px) {
          .storm-stop-card {
            flex-direction: column;
          }
          .stop-order-badge {
            align-self: flex-start;
          }
          .stop-actions {
            flex-direction: row;
            width: 100%;
          }
          .stop-complete-btn {
            flex: 1;
          }
          .route-header {
            gap: 6px;
          }
          .route-title {
            font-size: 1.2rem;
          }
          .route-header-actions {
            flex-wrap: wrap;
            gap: 6px;
          }
          .route-header-actions .btn {
            flex: 1 1 auto;
            min-width: 0;
            text-align: center;
          }
        }

        /* Route Complete Actions */
        .total-amount {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--color-text);
          margin-bottom: 24px !important;
        }
        .complete-actions {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
        }

        /* Print View Styles */
        .print-view {
          display: none;
        }

        /* Print Media Query */
        @media print {
          /* Hide everything except print view */
          .storm-mode-page > *:not(.print-view) {
            display: none !important;
          }

          .print-view {
            display: block !important;
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            background: white;
            padding: 20px;
            box-sizing: border-box;
          }

          .print-header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #333;
            padding-bottom: 20px;
          }
          .print-header h1 {
            margin: 0 0 10px 0;
            font-size: 24px;
            color: #333;
          }
          .print-meta {
            display: flex;
            justify-content: center;
            gap: 40px;
          }
          .print-meta p {
            margin: 5px 0;
            font-size: 14px;
          }

          .print-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
          }
          .print-table th,
          .print-table td {
            padding: 10px 12px;
            text-align: left;
            border-bottom: 1px solid #ddd;
          }
          .print-table th {
            background: #f5f5f5;
            font-weight: 600;
            border-bottom: 2px solid #333;
          }
          .print-table tbody tr:nth-child(even) {
            background: #fafafa;
          }
          .print-table tfoot td {
            border-top: 2px solid #333;
            font-size: 16px;
            padding-top: 15px;
          }
          .text-right {
            text-align: right;
          }

          .print-footer {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            text-align: center;
            font-size: 12px;
            color: var(--color-text-light);
          }
          .print-footer p {
            margin: 5px 0;
          }

          .print-expenses-section {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 2px solid #e5e7eb;
          }
          .print-expenses-section h3 {
            font-size: 14px;
            margin-bottom: 10px;
            color: #374151;
          }
          .expenses-table {
            font-size: 11px;
          }
          .expenses-table td {
            padding: 6px 8px;
          }

          .print-profit-summary {
            margin-top: 20px;
            padding: 15px;
            background: #f9fafb;
            border-radius: 8px;
            border: 1px solid #e5e7eb;
          }
          .profit-row {
            display: flex;
            justify-content: space-between;
            padding: 6px 0;
            font-size: 13px;
          }
          .profit-row.expense {
            color: var(--color-error);
          }
          .profit-row.expense-header {
            font-weight: 600;
            margin-top: 8px;
            padding-bottom: 4px;
            border-bottom: 1px solid #e5e7eb;
          }
          .profit-row.expense-item {
            padding-left: 12px;
            font-size: 12px;
          }
          .profit-row.expense-item .expense-detail {
            text-transform: capitalize;
            color: #6b7280;
          }
          .profit-row.expense-total {
            padding-left: 12px;
            margin-top: 4px;
            padding-top: 4px;
            border-top: 1px dashed #d1d5db;
            font-weight: 500;
          }
          .profit-row.net {
            border-top: 2px solid #374151;
            margin-top: 8px;
            padding-top: 10px;
            font-size: 15px;
          }
          .profit-amount {
            font-family: monospace;
          }

          .no-print {
            display: none !important;
          }
        }

        /* Screen-only print preview */
        @media screen {
          .print-view {
            display: none;
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: white;
            z-index: 1000;
            overflow: auto;
            padding: 40px;
          }
          .print-view:target,
          .storm-mode-page .print-view {
            display: block;
          }

          .print-header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #333;
            padding-bottom: 20px;
          }
          .print-header h1 {
            margin: 0 0 10px 0;
            font-size: 28px;
            color: #333;
          }
          .print-meta {
            display: flex;
            justify-content: center;
            gap: 40px;
          }
          .print-meta p {
            margin: 5px 0;
            font-size: 14px;
          }

          .print-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
          }
          .print-table th,
          .print-table td {
            padding: 12px 16px;
            text-align: left;
            border-bottom: 1px solid #ddd;
          }
          .print-table th {
            background: #f5f5f5;
            font-weight: 600;
            border-bottom: 2px solid #333;
          }
          .print-table tbody tr:nth-child(even) {
            background: #fafafa;
          }
          .print-table tfoot td {
            border-top: 2px solid #333;
            font-size: 18px;
            padding-top: 15px;
          }
          .text-right {
            text-align: right;
          }

          .print-footer {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            text-align: center;
            font-size: 14px;
            color: var(--color-text-light);
          }
          .print-footer p {
            margin: 5px 0;
          }

          .print-close-btn {
            position: fixed;
            top: 20px;
            right: 20px;
            background: #333;
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 8px;
            cursor: pointer;
            font-size: 14px;
          }
          .print-close-btn:hover {
            background: #555;
          }
        }

        /* Add Customer Modal */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
          overflow: hidden;
        }
        .add-customer-modal {
          background: var(--color-card);
          border-radius: 16px;
          width: 100%;
          max-width: 500px;
          max-height: 85vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 40px rgba(0,0,0,0.3);
          overflow: hidden;
        }
        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px;
          border-bottom: 1px solid var(--color-border);
        }
        .modal-header h2 {
          margin: 0;
          font-size: 1.25rem;
        }
        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: var(--color-text-light);
          padding: 4px 8px;
        }
        .modal-close:hover {
          color: var(--color-text);
        }
        .modal-body {
          padding: 20px;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          flex: 1;
          -webkit-overflow-scrolling: touch;
        }
        .customer-search-input {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid var(--color-border);
          border-radius: 8px;
          font-size: 1rem;
          margin-bottom: 16px;
        }
        .customer-search-input:focus {
          outline: none;
          border-color: var(--color-secondary);
        }
        .customer-list {
          overflow-y: auto;
          max-height: 400px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .no-customers {
          text-align: center;
          padding: 40px 20px;
          color: var(--color-text-light);
        }
        .customer-select-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          background: var(--color-background);
          border: 2px solid transparent;
          border-radius: 10px;
          cursor: pointer;
          text-align: left;
          transition: all 0.15s ease;
        }
        .customer-select-item:hover {
          border-color: var(--color-secondary);
          background: var(--color-card);
        }
        .customer-select-item:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .customer-info {
          flex: 1;
        }
        .customer-name {
          font-weight: 600;
          margin-bottom: 4px;
        }
        .customer-address {
          font-size: 0.875rem;
          color: var(--color-text-light);
        }
        .add-indicator {
          width: 32px;
          height: 32px;
          background: var(--color-secondary);
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.25rem;
          font-weight: 700;
          flex-shrink: 0;
        }

        /* New Customer Form Styles */
        .new-customer-btn {
          width: 100%;
          padding: 16px;
          background: linear-gradient(135deg, var(--color-success) 0%, #1b7a3d 100%);
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .new-customer-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(34, 139, 34, 0.3);
        }
        .modal-divider {
          display: flex;
          align-items: center;
          text-align: center;
          margin: 16px 0;
          color: var(--color-text-light);
          font-size: 0.875rem;
        }
        .modal-divider::before,
        .modal-divider::after {
          content: '';
          flex: 1;
          border-bottom: 1px solid var(--color-border);
        }
        .modal-divider span {
          padding: 0 12px;
        }
        .new-customer-form {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .new-customer-form input {
          padding: 12px 16px;
          border: 2px solid var(--color-border);
          border-radius: 8px;
          font-size: 1rem;
          width: 100%;
          box-sizing: border-box;
        }
        .new-customer-form input:focus {
          outline: none;
          border-color: var(--color-secondary);
        }
        .form-row {
          display: flex;
          gap: 12px;
        }
        .form-row input {
          flex: 1;
        }
        .form-row-3 {
          display: flex;
          gap: 12px;
        }
        .form-row-3 input:first-child {
          flex: 2;
        }
        .back-btn {
          background: transparent;
          border: none;
          color: var(--color-secondary);
          padding: 8px 0;
          font-size: 0.875rem;
          cursor: pointer;
          text-align: left;
          margin-bottom: 8px;
        }
        .back-btn:hover {
          text-decoration: underline;
        }
        .create-customer-btn {
          margin-top: 8px;
          padding: 16px;
          font-size: 1rem;
        }
        .form-section-title {
          font-weight: 600;
          color: var(--color-text);
          margin: 12px 0 4px;
          padding-bottom: 8px;
          border-bottom: 1px solid var(--color-border);
          font-size: 0.9rem;
        }
        .form-field {
          display: flex;
          flex-direction: column;
          gap: 4px;
          flex: 1;
        }
        .form-field-label {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--color-text-light);
          text-transform: uppercase;
        }
        .new-customer-form select {
          padding: 12px 16px;
          border: 2px solid var(--color-border);
          border-radius: 8px;
          font-size: 1rem;
          width: 100%;
          box-sizing: border-box;
          background: white;
          cursor: pointer;
        }
        .new-customer-form select:focus {
          outline: none;
          border-color: var(--color-secondary);
        }
        .new-customer-form textarea {
          padding: 12px 16px;
          border: 2px solid var(--color-border);
          border-radius: 8px;
          font-size: 1rem;
          width: 100%;
          box-sizing: border-box;
          font-family: inherit;
          resize: vertical;
        }
        .new-customer-form textarea:focus {
          outline: none;
          border-color: var(--color-secondary);
        }
        .price-input-wrapper {
          display: flex;
          align-items: center;
        }
        .price-prefix {
          background: var(--color-background);
          padding: 12px 12px;
          border: 2px solid var(--color-border);
          border-right: none;
          border-radius: 8px 0 0 8px;
          color: var(--color-text-light);
          font-weight: 500;
        }
        .price-input-wrapper input {
          border-radius: 0 8px 8px 0 !important;
          flex: 1;
        }
        /* Checkbox styles now in index.css */

        /* Start Route Modal */
        .start-route-modal {
          background: var(--color-card);
          border-radius: 16px;
          width: 100%;
          max-width: 400px;
          padding: 24px;
          box-shadow: 0 20px 40px rgba(0,0,0,0.3);
        }
        .start-route-modal-header {
          text-align: center;
          margin-bottom: 20px;
        }
        .start-route-modal-header h2 {
          margin: 0 0 8px;
          font-size: 1.25rem;
          color: var(--color-text);
        }
        .start-route-name {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--color-secondary);
          margin: 0;
        }
        .start-route-modal-body {
          margin-bottom: 24px;
        }
        .start-route-question {
          font-size: 0.95rem;
          color: var(--color-text-light);
          margin: 0 0 16px;
        }
        .start-location-option {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 14px 16px;
          border: 2px solid var(--color-border);
          border-radius: 10px;
          margin-bottom: 12px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .start-location-option:hover {
          border-color: var(--color-primary);
          background: rgba(37, 99, 235, 0.03);
        }
        .start-location-option.selected {
          border-color: var(--color-primary);
          background: rgba(37, 99, 235, 0.08);
        }
        .start-location-option input[type="radio"] {
          width: 20px;
          height: 20px;
          margin-top: 2px;
          accent-color: var(--color-primary);
        }
        .start-location-option .option-content {
          flex: 1;
        }
        .start-location-option .option-label {
          display: block;
          font-weight: 600;
          color: var(--color-text);
          margin-bottom: 4px;
        }
        .start-location-option .option-address {
          display: block;
          font-size: 0.875rem;
          color: var(--color-text-light);
        }
        .start-location-option .option-address.gps-address {
          color: var(--color-primary);
        }
        .start-route-modal-footer {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }
        .start-route-modal-footer .btn {
          min-width: 100px;
        }

        /* Payment Modal */
        .payment-modal {
          background: var(--color-card);
          border-radius: 20px;
          width: 100%;
          max-width: 340px;
          max-height: 90vh;
          overflow-y: auto;
          padding: 24px;
          text-align: center;
          box-shadow: 0 20px 40px rgba(0,0,0,0.3);
        }
        .payment-modal-header h2 {
          margin: 0 0 8px;
          font-size: 1.25rem;
        }
        .payment-customer-name {
          color: var(--color-text);
          font-weight: 600;
          margin: 0 0 4px;
        }
        .payment-amount {
          color: var(--color-success);
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0 0 16px;
        }
        .payment-amount .original-price-strike {
          text-decoration: line-through;
          color: #9ca3af;
          font-size: 1rem;
          margin-right: 8px;
        }
        .payment-amount .discounted-price-display {
          color: var(--color-success, #059669);
        }
        .salt-indicator-modal {
          font-size: 0.85rem;
          color: #3b82f6;
          font-weight: 500;
          margin-left: 4px;
        }

        /* Salt Toggle Section in Payment Modal */
        .salt-toggle-section {
          margin-bottom: 16px;
          padding: 12px;
          background: #eff6ff;
          border-radius: 8px;
          border: 1px solid #bfdbfe;
        }
        .salt-toggle-label {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          font-size: 0.95rem;
        }
        .salt-toggle-label input[type="checkbox"] {
          width: 20px;
          height: 20px;
          cursor: pointer;
        }
        .salt-toggle-text {
          color: #1e40af;
          font-weight: 500;
        }

        .addons-indicator-modal {
          color: #7c3aed;
          font-size: 0.75rem;
          font-weight: 600;
        }

        /* Service Add-ons Section in Payment Modal */
        .service-addons-section {
          margin-bottom: 16px;
          padding: 12px;
          background: #f5f3ff;
          border-radius: 8px;
          border: 1px solid #ddd6fe;
        }
        .service-addons-title {
          display: block;
          font-size: 0.85rem;
          font-weight: 600;
          color: #5b21b6;
          margin-bottom: 10px;
        }
        .service-addons-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .service-addon-item {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          font-size: 0.9rem;
          padding: 8px 12px;
          background: white;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
          transition: all 0.15s ease;
        }
        .service-addon-item:has(input:checked) {
          background: #ede9fe;
          border-color: #c4b5fd;
        }
        .service-addon-item input[type="checkbox"] {
          width: 16px;
          height: 16px;
          cursor: pointer;
          accent-color: #7c3aed;
        }
        .addon-name {
          flex: 1;
          color: #374151;
          font-weight: 500;
        }
        .addon-price {
          color: #7c3aed;
          font-weight: 600;
          font-size: 0.85rem;
        }
        .addons-total {
          margin-top: 10px;
          padding-top: 10px;
          border-top: 1px solid #ddd6fe;
          text-align: right;
          font-weight: 600;
          color: #5b21b6;
          font-size: 0.9rem;
        }

        /* Adjustments Section in Payment Modal (Discount + Extra Charge) */
        .adjustments-section {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 16px;
          padding-bottom: 16px;
          border-bottom: 1px solid var(--color-border);
        }
        .adjustment-item {
          width: 100%;
        }
        .add-adjustment-btn {
          background: none;
          border: 1px dashed var(--color-border);
          border-radius: 8px;
          padding: 10px 12px;
          color: var(--color-text-light);
          cursor: pointer;
          font-size: 0.85rem;
          width: 100%;
          text-align: center;
        }
        .add-adjustment-btn:hover {
          border-color: var(--color-primary);
          color: var(--color-primary);
        }
        .add-extra-charge-btn:hover {
          border-color: #22c55e;
          color: #22c55e;
        }
        .adjustment-form {
          background: var(--color-background-alt);
          border-radius: 8px;
          padding: 10px;
          border: 1px solid var(--color-border);
        }
        .adjustment-form-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }
        .adjustment-label {
          font-size: 0.8rem;
          font-weight: 600;
          color: var(--color-text);
        }
        .remove-adjustment-btn {
          background: none;
          border: none;
          color: #9ca3af;
          font-size: 1rem;
          cursor: pointer;
          padding: 0 4px;
          line-height: 1;
        }
        .remove-adjustment-btn:hover {
          color: var(--color-error);
        }
        .discount-form-row {
          display: flex;
          gap: 8px;
          margin-bottom: 8px;
        }
        .discount-type-select {
          flex: 1;
          padding: 8px;
          border: 1px solid var(--color-border);
          border-radius: 6px;
          font-size: 0.85rem;
        }
        .discount-amount-input {
          flex: 1;
          padding: 8px;
          border: 1px solid var(--color-border);
          border-radius: 6px;
          font-size: 0.85rem;
        }
        .adjustment-reason-input {
          width: 100%;
          padding: 8px;
          border: 1px solid var(--color-border);
          border-radius: 6px;
          font-size: 0.85rem;
          margin-bottom: 8px;
          box-sizing: border-box;
        }
        .discount-ongoing-label {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 0.85rem;
          color: var(--color-text-light);
          cursor: pointer;
          padding: 6px 0;
          margin-top: 6px;
          background: transparent;
          border: none;
        }
        .discount-ongoing-label:hover {
          color: var(--color-text);
        }
        .discount-ongoing-label input[type="checkbox"] {
          width: 18px;
          height: 18px;
          accent-color: var(--color-primary);
          cursor: pointer;
          flex-shrink: 0;
          margin: 0;
        }
        .discount-ongoing-label span {
          user-select: none;
        }
        .remove-discount-link {
          background: none;
          border: none;
          color: var(--color-error);
          font-size: 0.8rem;
          cursor: pointer;
          padding: 4px 0;
          text-decoration: underline;
        }
        .remove-discount-link:hover {
          color: #b91c1c;
        }
        .extra-charge-form-row {
          display: flex;
          align-items: center;
          gap: 4px;
          margin-bottom: 8px;
        }
        .extra-charge-prefix {
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--color-text);
        }
        .extra-charge-amount-input {
          flex: 1;
          padding: 8px;
          border: 1px solid var(--color-border);
          border-radius: 6px;
          font-size: 0.85rem;
        }
        .extra-charge-indicator-modal {
          font-size: 0.75rem;
          color: #22c55e;
          margin-left: 4px;
        }

        /* Photos: top of the completion modal, sized for gloves */
        .stop-photo-section {
          margin: 12px 0 4px;
        }
        .take-photo-btn {
          width: 100%;
          min-height: 56px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .take-photo-btn:disabled {
          opacity: 0.75;
          cursor: wait;
        }
        .photo-spinner {
          display: inline-block;
          width: 20px;
          height: 20px;
          border: 3px solid rgba(255, 255, 255, 0.4);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        .photo-thumbnails {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-top: 10px;
        }
        .photo-thumbnail-container {
          position: relative;
          width: 72px;
          height: 72px;
        }
        .photo-thumbnail.photo-uploading {
          opacity: 0.6;
        }
        .photo-thumbnail.photo-failed {
          opacity: 0.5;
          border-color: var(--color-error);
        }
        .photo-thumb-overlay {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 6px;
        }
        .photo-retry-btn {
          width: 100%;
          height: 100%;
          border: 2px solid var(--color-error);
          background: rgba(0, 0, 0, 0.45);
          color: white;
          font-size: 0.95rem;
          font-weight: 700;
          cursor: pointer;
        }
        .photo-thumbnail {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 6px;
          cursor: pointer;
          border: 1px solid var(--color-border);
        }
        .photo-thumbnail:hover {
          border-color: var(--color-primary);
        }
        .delete-photo-btn {
          position: absolute;
          top: -10px;
          right: -10px;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          background: var(--color-error);
          color: white;
          border: 2px solid white;
          font-size: 14px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          line-height: 1;
        }
        .delete-photo-btn:hover {
          background: #b91c1c;
        }

        /* Photo Preview Modal */
        .photo-preview-overlay {
          background: rgba(0, 0, 0, 0.9);
        }
        .photo-preview-modal {
          position: relative;
          max-width: 90vw;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .photo-preview-close {
          position: absolute;
          top: -40px;
          right: 0;
          background: transparent;
          border: none;
          color: white;
          font-size: 24px;
          cursor: pointer;
          padding: 8px;
        }
        .photo-preview-image {
          max-width: 100%;
          max-height: 80vh;
          object-fit: contain;
          border-radius: 8px;
        }
        .photo-preview-caption {
          color: white;
          margin-top: 12px;
          font-size: 0.9rem;
        }

        .payment-options {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        .payment-option-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 20px 16px;
          border: 2px solid var(--color-border);
          border-radius: 12px;
          background: var(--color-background);
          cursor: pointer;
          font-size: 0.9rem;
          font-weight: 600;
          transition: all 0.15s ease;
        }
        .payment-option-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }
        .payment-option-btn .payment-icon {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.25rem;
          font-weight: 700;
        }
        /* Square (blue - Square brand color) */
        .payment-option-btn.square {
          border-color: #006aff;
          color: #006aff;
        }
        .payment-option-btn.square .payment-icon {
          background: rgba(0, 106, 255, 0.1);
        }
        .payment-option-btn.square:hover {
          background: rgba(0, 106, 255, 0.05);
        }
        /* Venmo - Paid (green) */
        .payment-option-btn.venmo-paid {
          border-color: #22c55e;
          color: #22c55e;
        }
        .payment-option-btn.venmo-paid .payment-icon {
          background: rgba(34, 197, 94, 0.1);
        }
        .payment-option-btn.venmo-paid:hover {
          background: rgba(34, 197, 94, 0.05);
        }
        /* Venmo - Not Paid (orange/amber) */
        .payment-option-btn.venmo-not-paid {
          border-color: #f59e0b;
          color: #d97706;
        }
        .payment-option-btn.venmo-not-paid .payment-icon {
          background: rgba(245, 158, 11, 0.1);
        }
        .payment-option-btn.venmo-not-paid:hover {
          background: rgba(245, 158, 11, 0.05);
        }
        /* Cash App - Paid (green) */
        .payment-option-btn.cashapp-paid {
          border-color: #22c55e;
          color: #22c55e;
        }
        .payment-option-btn.cashapp-paid .payment-icon {
          background: rgba(34, 197, 94, 0.1);
        }
        .payment-option-btn.cashapp-paid:hover {
          background: rgba(34, 197, 94, 0.05);
        }
        /* Cash App - Not Paid (orange/amber) */
        .payment-option-btn.cashapp-not-paid {
          border-color: #f59e0b;
          color: #d97706;
        }
        .payment-option-btn.cashapp-not-paid .payment-icon {
          background: rgba(245, 158, 11, 0.1);
        }
        .payment-option-btn.cashapp-not-paid:hover {
          background: rgba(245, 158, 11, 0.05);
        }
        /* Cash - Paid (green) */
        .payment-option-btn.cash-paid {
          border-color: #22c55e;
          color: #22c55e;
        }
        .payment-option-btn.cash-paid .payment-icon {
          background: rgba(34, 197, 94, 0.1);
        }
        .payment-option-btn.cash-paid:hover {
          background: rgba(34, 197, 94, 0.05);
        }
        /* Cash - Not Paid, and any custom method's Not Paid (orange/amber) */
        .payment-option-btn.cash-not-paid,
        .payment-option-btn.custom-not-paid {
          border-color: #f59e0b;
          color: #d97706;
        }
        .payment-option-btn.cash-not-paid .payment-icon,
        .payment-option-btn.custom-not-paid .payment-icon {
          background: rgba(245, 158, 11, 0.1);
        }
        .payment-option-btn.cash-not-paid:hover,
        .payment-option-btn.custom-not-paid:hover {
          background: rgba(245, 158, 11, 0.05);
        }
        /* A custom method - Paid (green, like Cash) */
        .payment-option-btn.custom-paid {
          border-color: #22c55e;
          color: #22c55e;
        }
        .payment-option-btn.custom-paid .payment-icon {
          background: rgba(34, 197, 94, 0.1);
        }
        .payment-option-btn.custom-paid:hover {
          background: rgba(34, 197, 94, 0.05);
        }
        /* Not Paid (gray) */
        .payment-option-btn.not-paid {
          border-color: #9ca3af;
          color: #6b7280;
        }
        .payment-option-btn.not-paid .payment-icon {
          background: rgba(156, 163, 175, 0.1);
        }
        .payment-option-btn.not-paid:hover {
          background: rgba(156, 163, 175, 0.05);
        }

        /* Mark as Skipped Button (orange/warning) */
        .status-change-options {
          display: flex;
          justify-content: center;
          padding: 10px 0;
        }
        .payment-option-btn.mark-skipped-btn {
          border-color: #f97316;
          color: #ea580c;
          background: rgba(249, 115, 22, 0.1);
          min-width: 180px;
        }
        .payment-option-btn.mark-skipped-btn .payment-icon {
          background: rgba(249, 115, 22, 0.2);
          color: #ea580c;
        }
        .payment-option-btn.mark-skipped-btn:hover {
          background: rgba(249, 115, 22, 0.2);
          border-color: #ea580c;
        }

        /* Comp Submit Button (green) */
        .comp-submit-section {
          display: flex;
          justify-content: center;
          padding: 10px 0;
        }
        .payment-option-btn.comp-submit-btn {
          border-color: #22c55e;
          color: #15803d;
          background: rgba(34, 197, 94, 0.1);
          min-width: 200px;
          font-weight: 600;
        }
        .payment-option-btn.comp-submit-btn .payment-icon {
          background: rgba(34, 197, 94, 0.2);
          color: #15803d;
        }
        .payment-option-btn.comp-submit-btn:hover {
          background: rgba(34, 197, 94, 0.2);
          border-color: #16a34a;
        }

        /* Save Adjustments Button */
        .save-adjustments-section {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 15px 0;
          border-bottom: 1px solid #333;
          margin-bottom: 10px;
        }
        .save-adjustments-btn {
          background: linear-gradient(135deg, #3b82f6, #2563eb);
          color: white;
          border: none;
          padding: 12px 30px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          min-width: 180px;
        }
        .save-adjustments-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #2563eb, #1d4ed8);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }
        .save-adjustments-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .save-adjustments-hint {
          color: #9ca3af;
          font-size: 12px;
          margin-top: 8px;
          text-align: center;
        }

        /* Zelle - Paid (purple) */
        .payment-option-btn.zelle-paid {
          border-color: #7c3aed;
          color: #7c3aed;
        }
        .payment-option-btn.zelle-paid .payment-icon {
          background: rgba(124, 58, 237, 0.1);
        }
        .payment-option-btn.zelle-paid:hover {
          background: rgba(124, 58, 237, 0.05);
        }

        /* Zelle - Not Paid (orange/amber) */
        .payment-option-btn.zelle-not-paid {
          border-color: #f59e0b;
          color: #d97706;
        }
        .payment-option-btn.zelle-not-paid .payment-icon {
          background: rgba(245, 158, 11, 0.1);
        }
        .payment-option-btn.zelle-not-paid:hover {
          background: rgba(245, 158, 11, 0.05);
        }

        /* Check - Paid (teal) */
        .payment-option-btn.check-paid {
          border-color: #0d9488;
          color: #0d9488;
        }
        .payment-option-btn.check-paid .payment-icon {
          background: rgba(13, 148, 136, 0.1);
        }
        .payment-option-btn.check-paid:hover {
          background: rgba(13, 148, 136, 0.05);
        }

        /* Check - Not Paid (orange/amber) */
        .payment-option-btn.check-not-paid {
          border-color: #f59e0b;
          color: #d97706;
        }
        .payment-option-btn.check-not-paid .payment-icon {
          background: rgba(245, 158, 11, 0.1);
        }
        .payment-option-btn.check-not-paid:hover {
          background: rgba(245, 158, 11, 0.05);
        }

        /* Payment Section Divider */
        .payment-section-divider {
          display: flex;
          align-items: center;
          margin: 16px 0 12px 0;
          color: #6b7280;
          font-size: 0.85rem;
          font-weight: 500;
        }
        .payment-section-divider::before,
        .payment-section-divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: #e5e7eb;
        }
        .payment-section-divider span {
          padding: 0 12px;
        }

        /* Invoice Payment Options */
        .invoice-payment-options {
          display: flex;
          justify-content: center;
          gap: 12px;
        }
        .invoice-payment-options .payment-option-btn {
          flex: 0 0 auto;
          min-width: 120px;
        }

        /* Payment Badge */
        .payment-badge {
          font-size: 0.7rem;
          padding: 3px 8px;
          border-radius: 10px;
          font-weight: 600;
          white-space: nowrap;
          margin-left: auto;
          flex-shrink: 0;
        }
        .payment-badge.paid {
          background: rgba(34, 197, 94, 0.15);
          color: #15803d;
        }
        .payment-badge.invoice-sent {
          background: rgba(234, 179, 8, 0.15);
          color: #a16207;
        }
        .payment-badge.not-paid {
          background: rgba(156, 163, 175, 0.15);
          color: #6b7280;
        }
        .payment-badge.skipped {
          background: rgba(156, 163, 175, 0.15);
          color: #6b7280;
        }

        /* Square Badge States */
        .square-badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 0.7rem;
          font-weight: 600;
          margin-top: 4px;
        }
        .square-badge.paid {
          background: rgba(16, 185, 129, 0.15);
          color: var(--color-success, #059669);
        }
        .square-badge.invoice-sent {
          background: rgba(139, 92, 246, 0.15);
          color: #7c3aed;
        }
        .square-badge.pending {
          background: rgba(107, 114, 128, 0.15);
          color: #6b7280;
        }

        /* Billing Modal */
        .billing-modal {
          background: white;
          border-radius: 16px;
          width: 90%;
          max-width: 480px;
          max-height: 85vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        .billing-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid var(--color-border);
        }
        .billing-modal-header h2 {
          margin: 0;
          font-size: 1.25rem;
          font-weight: 600;
        }
        .billing-modal-content {
          padding: 16px 20px;
          overflow-y: auto;
          flex: 1;
        }
        .billing-modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 16px 20px;
          border-top: 1px solid var(--color-border);
        }
        .billing-section {
          margin-bottom: 20px;
        }
        .billing-section:last-child {
          margin-bottom: 0;
        }
        .billing-section-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.9rem;
          font-weight: 600;
          margin-bottom: 12px;
          color: var(--color-text);
        }
        .billing-section-title.warning {
          color: #d97706;
        }
        .billing-section-title.manual {
          color: #6b7280;
        }
        .billing-icon {
          font-size: 1rem;
        }
        .billing-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          background: #f9fafb;
          border-radius: 8px;
          margin-bottom: 8px;
          font-size: 0.9rem;
        }
        .billing-item:last-child {
          margin-bottom: 0;
        }
        .billing-item.auto-bill {
          background: rgba(16, 185, 129, 0.08);
        }
        .billing-item.no-email {
          background: rgba(245, 158, 11, 0.08);
        }
        .billing-item.manual {
          background: rgba(107, 114, 128, 0.08);
        }
        .billing-check {
          color: #10b981;
          font-size: 1rem;
        }
        .billing-warning {
          color: #f59e0b;
          font-size: 1rem;
        }
        .billing-manual {
          color: #6b7280;
          font-size: 0.9rem;
        }
        .billing-name {
          flex: 1;
          font-weight: 500;
        }
        .billing-amount {
          font-weight: 600;
          color: var(--color-text);
        }
        .billing-method {
          font-size: 0.8rem;
          color: var(--color-text-light);
          background: rgba(0,0,0,0.05);
          padding: 2px 8px;
          border-radius: 4px;
        }
        .no-billable {
          text-align: center;
          color: var(--color-text-light);
          padding: 24px;
        }

        /* Billing Results */
        .billing-results {
          padding: 8px 0;
        }
        .billing-results-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid #e5e7eb;
        }
        .billing-results-header h3 {
          font-size: 1rem;
          font-weight: 600;
          color: #374151;
          margin: 0;
        }
        .results-summary {
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 600;
        }
        .results-summary.success {
          background: rgba(16, 185, 129, 0.15);
          color: var(--color-success, #059669);
        }
        .results-summary.failed {
          background: rgba(239, 68, 68, 0.15);
          color: var(--color-error);
        }
        .billing-results-list {
          max-height: 300px;
          overflow-y: auto;
        }
        .billing-result-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px;
          background: #f9fafb;
          border-radius: 8px;
          margin-bottom: 8px;
          font-size: 0.85rem;
        }
        .billing-result-item.success {
          border-left: 3px solid #10b981;
        }
        .billing-result-item.failed {
          border-left: 3px solid #ef4444;
        }
        .billing-result-item .result-icon {
          font-weight: bold;
          font-size: 1rem;
        }
        .billing-result-item.success .result-icon {
          color: #10b981;
        }
        .billing-result-item.failed .result-icon {
          color: #ef4444;
        }
        .result-name {
          font-weight: 500;
          color: #374151;
        }
        .result-amount {
          margin-left: auto;
          font-weight: 600;
          color: #10b981;
        }
        .result-status {
          font-size: 0.75rem;
          color: #6b7280;
        }
        .result-reason {
          margin-left: auto;
          font-size: 0.8rem;
          color: #ef4444;
        }
        .billing-error-message {
          background: #fef2f2;
          border-left: 3px solid #ef4444;
          border-radius: 8px;
          color: #991b1b;
          font-size: 0.9rem;
          line-height: 1.5;
          margin: 0 0 12px;
          padding: 12px 14px;
        }
        .no-results {
          text-align: center;
          color: var(--color-text-light);
          padding: 24px;
        }
        .done-btn {
          width: 100%;
        }
      `}</style>
      <AlertModal
        show={alertModal.show}
        title={alertModal.title}
        message={alertModal.message}
        onClose={() => setAlertModal({ show: false, title: '', message: '' })}
      />
      <DepthEntryModal
        show={showDepthModal && !!selectedRoute}
        route={selectedRoute}
        routes={routes}
        onClose={() => setShowDepthModal(false)}
        onSaved={() => refreshAfterDepth()}
      />

      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        confirmColor={confirmModal.confirmColor}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal(prev => ({ ...prev, show: false }))}
      />
    </div>
  );
}

export default StormMode;
